import { useState, useEffect } from "react";
import { langPath, switchLangUrl, saveLangPref } from "./utils/lang";
import { setPageMeta } from "./utils/meta";
import reason01 from "./assets/reason01.png";
import reason02 from "./assets/reason02.png";
import reason03Preview from "./assets/nunt-reason03-media.png";
import scene01 from "./assets/scene01.png";
import scene02 from "./assets/scene02.png";
import scene03 from "./assets/scene03.png";
import collab from "./assets/collab.png";
import partnerCollab from "./assets/nunt-partner-collab.png";

const Placeholder = ({ width, height, label, style = {}, className = "" }) => (
  <div
    className={className}
    style={{
      width, height,
      background: "#d9d9d9",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      color: "#888",
      flexShrink: 0,
      ...style,
    }}
  >
    {label}
  </div>
);

const iconPaths = {
  document: [
    "M7 3h7l5 5v13H7z",
    "M14 3v6h5",
    "M10 13h6",
    "M10 17h5",
  ],
  chat: [
    "M5 6.5h14v9.5H9l-4 3v-12.5z",
    "M9 11h.01",
    "M12 11h.01",
    "M15 11h.01",
  ],
  users: [
    "M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
    "M16 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
    "M3.5 20a5 5 0 0 1 9 0",
    "M11.5 20a5 5 0 0 1 9 0",
  ],
  yen: [
    "M6 4l6 8 6-8",
    "M12 12v8",
    "M8 13h8",
    "M8 16h8",
  ],
  baht: [],
  calendar: [
    "M6 5h12v15H6z",
    "M8 3v4",
    "M16 3v4",
    "M6 9h12",
    "M9 13h.01",
    "M13 13h.01",
    "M17 13h.01",
  ],
  monitor: [
    "M4 5h16v11H4z",
    "M9 20h6",
    "M12 16v4",
    "M8 10l3 3 5-6",
  ],
  globe: [
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z",
    "M3 12h18",
    "M12 3a13 13 0 0 1 0 18",
    "M12 3a13 13 0 0 0 0 18",
  ],
  building: [
    "M5 21V6l9-3v18",
    "M14 9h5v12",
    "M8 9h2",
    "M8 13h2",
    "M8 17h2",
    "M17 13h.01",
    "M17 17h.01",
  ],
  laptop: [
    "M5 6h14v10H5z",
    "M3 19h18",
    "M8 10h3",
  ],
  send: [
    "M4 12l16-8-5 16-3-7-8-1z",
    "M12 13l8-9",
  ],
  video: [
    "M5 6h11v12H5z",
    "M16 10l4-3v10l-4-3",
  ],
  camera: [
    "M5 8h3l1.5-2h5L16 8h3v11H5z",
    "M12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z",
  ],
  image: [
    "M4 5h16v14H4z",
    "M8 13l2.5-3 3.5 4 2-2.5 4 5.5",
    "M8 9h.01",
  ],
  fileCheck: [
    "M7 3h7l5 5v13H7z",
    "M14 3v6h5",
    "M9.5 15l2 2 4-5",
  ],
  folder: [
    "M4 7h6l2 2h8v10H4z",
    "M4 10h16",
  ],
  shieldCheck: [
    "M12 3l7 3v5c0 4.5-2.8 8-7 10-4.2-2-7-5.5-7-10V6z",
    "M9 12l2 2 4-5",
  ],
  card: [
    "M4 7h16v10H4z",
    "M4 10h16",
    "M7 14h3",
  ],
  arrow: ["M5 12h14", "M13 6l6 6-6 6"],
  checklist: [
    "M7 5h10",
    "M7 10h10",
    "M7 15h10",
    "M4 5h.01",
    "M4 10h.01",
    "M4 15h.01",
  ],
};

const HeroIcon = ({ name, size = 22, strokeWidth = 1.8, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {name === "baht" ? (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <text
          x="12"
          y="15.4"
          textAnchor="middle"
          fontSize="10.5"
          fontWeight="700"
          fill="currentColor"
          stroke="none"
        >
          ฿
        </text>
      </>
    ) : (
      (iconPaths[name] || iconPaths.arrow).map((d) => (
        <path key={d} d={d} />
      ))
    )}
  </svg>
);

const MiniProgress = () => (
  <div className="preview-card-progress" aria-hidden="true">
    <span />
    <span />
    <span />
  </div>
);

export default function NuntLP({ language = 'th' }) {
  const [faqOpen, setFaqOpen] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { setPageMeta(language, '/'); }, [language]);

  const contact = langPath(language, '/contact');
  const download = langPath(language, '/download');
  const privacy = langPath(language, '/privacy');
  const root = langPath(language, '/');
  const handleLangSwitch = (lang) => { saveLangPref(lang); setMenuOpen(false); };
  const isEn = language === 'en';
  const isTh = language === 'th';

  /* 3言語切替ヘルパー */
  const t = (th, en, jp) => isTh ? th : isEn ? en : jp;

  const tr = {
    navAbout: t("เกี่ยวกับ Nunt", "About Nunt", "Nuntとは"),
    navReasons: t("เหตุผลที่เลือก Nunt", "Why Nunt", "選ばれる理由"),
    navCases: t("ตัวอย่างการใช้งาน", "Use Cases", "活用事例"),
    navFaq: t("FAQ", "FAQ", "よくある質問"),
    navCompany: t("บริษัท", "Company", "会社概要"),
    navContact: t("ปรึกษาฟรี", "Contact Us", "無料相談する"),
    navDownload: t("ดาวน์โหลดเอกสาร", "Download Materials", "資料ダウンロード"),
    heroEyebrow: t("บริการใช้ภาพทาเลนต์รายเดือนสำหรับบริษัทในไทย", "Monthly talent visual licensing for companies in Thailand", "タイ法人向け タレント素材の月額サービス"),
    heroEyebrowSP: t("บริการใช้ภาพทาเลนต์รายเดือนสำหรับบริษัทในไทย", "Talent visual licensing for Thailand", "タイ法人向け タレント素材の月額サービス"),
    heroCta1: t("ดาวน์โหลดเอกสาร", "Download Materials", "資料ダウンロード"),
    heroCta2: t("ปรึกษาฟรี", "Contact Us", "無料相談する"),
    heroSupportTitle: t("สำหรับทาเลนต์และเอเจนซี่", "For talents and agencies", "タレント・事務所の方へ"),
    heroSupportSub: t("เกี่ยวกับการร่วมงานและลงทะเบียน", "Partnership and registration", "提携・登録について"),
    heroSceneTitle: t("ตัวอย่างการใช้งาน", "Use Cases", "活用シーン"),
    heroSceneNote: t("นำไปใช้กับงานการตลาดได้หลากหลายรูปแบบ", "Available for a wide range of marketing uses.", "など、幅広くご利用いただけます。"),
    heroFlowTitle: t("ขั้นตอนการใช้งาน", "How It Works", "導入の流れ"),
    heroFlowNote: t("เริ่มใช้ภาพทาเลนต์ได้อย่างราบรื่นและรวดเร็ว", "Start using talent visuals smoothly and quickly.", "最短でスムーズにご利用いただけます。"),
    heroModelTitle1a: t("แบรนด์สกินแคร์", "Skincare Brand", "スキンケアブランド"),
    heroModelTitle1b: t("ภาพสำหรับเว็บไซต์และโฆษณา", "Website and Ad Visuals", "Web・広告素材"),
    heroModelTitle2a: t("โมเดลภาพลักษณ์แบรนด์", "Brand Image Model", "ブランドイメージモデル"),
    heroModelTitle2b: t("ภาพสำหรับ SNS และโฆษณา", "SNS and Ad Materials", "SNS・広告素材"),
    heroAudience1Title: t("สำหรับบริษัท", "For Companies", "企業の方へ"),
    heroAudience1SubPC: t("ภาพทาเลนต์ที่น่าเชื่อถือ พร้อมนำไปใช้ได้ทันที", "Reliable talent visuals, ready to use.", "安心して使えるタレント素材を、スピーディーに。"),
    heroAudience1SubSP: t("ภาพทาเลนต์ที่น่าเชื่อถือ พร้อมนำไปใช้ได้ทันที", "Reliable talent visuals, ready to use.", "安心して使える素材を、スピーディーに。"),
    heroAudience2Title: t("สำหรับทาเลนต์และเอเจนซี่", "For Talents and Agencies", "タレント・事務所の方へ"),
    heroAudience2Sub: t("ยินดีรับการลงทะเบียนและข้อเสนอความร่วมมือ", "We welcome registration and partnership inquiries.", "ご登録・ご提携を歓迎しています。"),
    aboutLabel: t("เกี่ยวกับ Nunt", "About Nunt", "Nuntとは"),
    aboutDownloadBtn: t("ดาวน์โหลดเอกสาร", "Download Materials", "資料をダウンロード"),
    aboutCompareHeading: t("การใช้ทาเลนต์แบบทั่วไป", "Traditional Talent Casting", "通常のタレント起用"),
    aboutCompareShoot: t("ถ่ายภาพ", "Photo Shoot", "撮影"),
    aboutCompareContract: t("สัญญา", "Contract", "契約"),
    aboutCompareMaterial: t("ผลิตภาพ", "Material Production", "素材制作"),
    aboutCompareCost: t("ค่าเริ่มต้น", "Initial Cost", "初期費用"),
    aboutNuntHeading: t("ใช้ Nunt", "Using Nunt", "Nunt利用"),
    aboutMonthly: t("แพ็กเกจรายเดือน", "Monthly Plan", "月額利用"),
    aboutInitialLabel: t("ค่าเริ่มต้น", "Initial Fee", "初期費用"),
    partnershipLabel: t("PARTNERSHIP MODEL", "PARTNERSHIP MODEL", "PARTNERSHIP MODEL / 提携モデル"),
    partnershipTalent: t("ทาเลนต์และเอเจนซี่", "Talent Partners", "タレント・事務所"),
    partnershipMaterial: t("ภาพที่ใช้ได้", "Visual Materials", "素材提供"),
    partnershipCondition: t("เงื่อนไขการใช้งาน", "Usage Conditions", "利用条件の確認"),
    partnershipNuntCopy1: t("Nunt จัดการภาพและสิทธิ์", "Nunt organizes the", "利用条件と素材を"),
    partnershipNuntCopy2: t("การใช้งานให้เป็นระบบ", "materials and usage rights", "Nuntが整理"),
    partnershipManage: t("บริหารพาร์ทเนอร์", "Partner Management", "提携管理"),
    partnershipOrganize: t("จัดระเบียบภาพ", "Material Organization", "素材整理"),
    partnershipScope: t("ตรวจสอบขอบเขตการใช้งาน", "Usage Scope Confirmation", "利用範囲確認"),
    partnershipProposal: t("เสนอให้บริษัท", "Company Proposals", "企業への提案"),
    partnershipCompany: t("บริษัท", "Companies", "企業"),
    partnershipConsult: t("ปรึกษาและสมัครใช้งาน", "Consultation and Application", "相談・申込"),
    partnershipUse: t("ใช้กับเว็บไซต์ SNS และโฆษณา", "Use for Websites, SNS, and Ads", "Web・SNS・広告で活用"),
    reasonsTitle: t("เหตุผลที่บริษัทเลือก Nunt", "Why companies choose Nunt", "Nuntが選ばれる3つの理由"),
    usecaseTitle: t("ตัวอย่างการใช้งานตามประเภทธุรกิจ", "Use Cases by Industry", "業種に合わせた活用イメージ"),
    usecaseCardLabel: t("ตัวอย่างการใช้งาน", "Use Case", "活用イメージ"),
    faqTitle: t("คำถามที่พบบ่อยก่อนติดต่อเรา", "Frequently Asked Questions Before Contacting Us", "ご相談前によくある質問"),
    partnerLabel: t("PARTNERSHIP", "PARTNERSHIP", "PARTNERSHIP"),
    partnerCta: t("ปรึกษาเรื่องความร่วมมือ", "Discuss Partnership", "協業について相談する"),
    aboutArrowNote: t("ลดค่าเริ่มต้นและขั้นตอนเตรียมงาน", "Reduce initial cost and preparation work", "初期費用と手間を抑えて"),
    footerAbout: t("เกี่ยวกับ Nunt", "About Nunt", "Nuntとは"),
    footerContact: t("ติดต่อเรา", "Contact", "お問合せ"),
    footerPrivacy: t("นโยบายความเป็นส่วนตัว", "Privacy Policy", "プライバシーポリシー"),
  };

  const navLinks = [
    [tr.navAbout, `${root}#about`],
    [tr.navReasons, `${root}#reasons`],
    [tr.navCases, `${root}#cases`],
    [tr.navFaq, `${root}#faq`],
    [tr.navCompany, contact],
  ];

  const benefitsPC = isTh ? [
    ["baht", "ค่าเริ่มต้น", "0 THB"],
    ["calendar", "จ่ายรายเดือน", "เท่านั้น"],
    ["monitor", "เว็บไซต์ SNS", "และโฆษณา"],
    ["globe", "เหมาะกับ", "ตลาดไทย"],
    ["building", "สำหรับบริษัท", "และพาร์ทเนอร์"],
  ] : isEn ? [
    ["baht", "Initial fee", "0 THB"],
    ["calendar", "Monthly plan", "only"],
    ["monitor", "Web, SNS,", "and ads"],
    ["globe", "For the Thai", "market"],
    ["building", "For companies", "and partners"],
  ] : [
    ["baht", "初期費用", "0 THB"],
    ["calendar", "月額のみ", ""],
    ["monitor", "Web / SNS /", "広告で利用可能"],
    ["globe", "タイ市場向け", ""],
    ["building", "企業向け /", "提携歓迎"],
  ];

  const benefitsSP = isTh ? [
    ["baht", "ค่าเริ่มต้น", "0 THB"],
    ["calendar", "จ่ายรายเดือนเท่านั้น", ""],
    ["monitor", "เว็บไซต์ SNS และโฆษณา", ""],
  ] : isEn ? [
    ["baht", "Initial fee", "0 THB"],
    ["calendar", "Monthly plan", "only"],
    ["monitor", "Web, SNS, and ads", ""],
  ] : [
    ["baht", "初期費用", "0 THB"],
    ["calendar", "月額のみ", ""],
    ["monitor", "Web・SNS・広告OK", ""],
  ];

  const sceneItems = isTh
    ? [["laptop", "เว็บไซต์"], ["send", "โพสต์และโฆษณา SNS"], ["video", "วิดีโอโฆษณา"]]
    : isEn
    ? [["laptop", "Website"], ["send", "SNS Posts and Ads"], ["video", "Video Ads"]]
    : [["laptop", "Webサイト"], ["send", "SNS投稿・広告"], ["video", "動画広告"]];

  const flowItems = isTh
    ? ["ปรึกษาเบื้องต้น", "เสนอแพ็กเกจและราคา", "ทำสัญญาและส่งมอบภาพ", "เริ่มใช้งานพร้อมการซัพพอร์ต"]
    : isEn
    ? ["Consultation", "Plan and Estimate", "Contract and Material Delivery", "Start Using with Support"]
    : ["ご相談・ヒアリング", "プランご提案・お見積り", "ご契約・素材のご提供", "ご利用開始・サポート"];

  const partnerChips = isTh
    ? ["ทาเลนต์เอเจนซี่", "อินฟลูเอนเซอร์", "เอเจนซี่โฆษณา", "บริษัทโปรดักชัน"]
    : isEn
    ? ["Talent Agencies", "Influencers", "Advertising Agencies", "Production Companies"]
    : ["タレント事務所", "インフルエンサー", "広告代理店", "制作会社"];

  const usecasesData = isTh ? [
    {
      label: "ความงามและคลินิก",
      desc: "สร้างความน่าเชื่อถือตั้งแต่แรกเห็นด้วยภาพทาเลนต์ ช่วยลดความลังเลของลูกค้าก่อนตัดสินใจจองหรือเข้ารับบริการ",
      img: scene01,
      chips: ["เว็บไซต์", "โฆษณา SNS", "แคมเปญ", "กระตุ้นการจอง"],
      examples: ["/sean01.png"],
    },
    {
      label: "ร้านอาหาร",
      desc: "ใช้ภาพที่สะดุดตาเพื่อเพิ่มภาพลักษณ์ของร้านและเมนู ช่วยกระตุ้นการเข้าร้านและการตอบสนองบนโซเชียลมีเดีย",
      img: scene02,
      chips: ["โพสต์ SNS", "แบนเนอร์โฆษณา", "โปรโมตเมนู", "กระตุ้นการเข้าร้าน"],
      examples: ["/sean02.png"],
    },
    {
      label: "ค้าปลีกและอีคอมเมิร์ซ",
      desc: "เพิ่มความเป็นมนุษย์ให้กับสินค้า ทำให้ภาพโฆษณาและแคมเปญสื่อสารได้เข้าใจง่ายขึ้น",
      img: scene03,
      chips: ["โฆษณาสินค้า", "Landing Page", "โฆษณา SNS", "แคมเปญ"],
      examples: ["/sean03.png"],
    },
  ] : isEn ? [
    {
      label: "Beauty and Clinics",
      desc: "Build a trustworthy first impression with talent visuals and help reduce customer hesitation before making an appointment.",
      img: scene01,
      chips: ["Website", "SNS Ads", "Campaign", "Appointment Promotion"],
      examples: ["/sean01.png"],
    },
    {
      label: "Restaurants",
      desc: "Use eye-catching visuals to improve the impression of your store and menu, and encourage visits or social media engagement.",
      img: scene02,
      chips: ["SNS Posts", "Ad Banners", "Menu Promotion", "Store Visits"],
      examples: ["/sean02.png"],
    },
    {
      label: "Retail and E-Commerce",
      desc: "Add a human impression to your products and make your advertising or campaign visuals easier to understand.",
      img: scene03,
      chips: ["Product Ads", "Landing Page", "SNS Ads", "Campaign"],
      examples: ["/sean03.png"],
    },
  ] : [
    {
      label: "美容・クリニック",
      desc: "信頼感のあるタレントビジュアルで、第一印象を整え、来院前の不安をやわらげます。",
      img: scene01,
      chips: ["Webサイト", "SNS広告", "キャンペーン", "来院促進"],
      examples: ["/sean01.png"],
    },
    {
      label: "飲食",
      desc: "目を引くビジュアルで店舗やメニューの印象を高め、来店やSNSでの反応につなげます。",
      img: scene02,
      chips: ["SNS投稿", "広告バナー", "メニュー訴求", "来店促進"],
      examples: ["/sean02.png"],
    },
    {
      label: "小売・EC",
      desc: "商品に人物の印象を加えることで、広告やキャンペーンで伝わりやすい見せ方ができます。",
      img: scene03,
      chips: ["商品広告", "LP", "SNS広告", "キャンペーン"],
      examples: ["/sean03.png"],
    },
  ];

  const faqs = isTh ? [
    {
      q: "ค่าใช้จ่ายประมาณเท่าไร?",
      a: "แพ็กเกจเริ่มต้นที่ 50,000 THB ราคาจะแตกต่างกันตามทาเลนต์ ประเภทธุรกิจ และระยะเวลาการใช้งาน สามารถติดต่อเราเพื่อปรึกษารายละเอียดได้",
    },
    {
      q: "มีระยะเวลาขั้นต่ำในการใช้งานไหม?",
      a: "ระยะเวลาการใช้งานขั้นต่ำคือ 3 เดือน โดยทั่วไป การใช้ทาเลนต์มักต้องทำสัญญารายปี ซึ่งอาจกลายเป็นภาระค่าใช้จ่ายสูงแม้ดูเหมือนจ่ายรายเดือนไม่มาก Nunt มีแพ็กเกจพิเศษสำหรับบริษัทที่ใช้งานครั้งแรก สามารถติดต่อเราเพื่อสอบถามได้",
    },
    {
      q: "สามารถใช้กับโฆษณาและโซเชียลมีเดียได้ไหม?",
      a: "ได้ สามารถใช้กับเว็บไซต์ โพสต์โซเชียล แบนเนอร์โฆษณา และการใช้งานอื่น ๆ ตามขอบเขตที่กำหนดในสัญญา",
    },
    {
      q: "ภาพและทาเลนต์ที่ใช้ได้จะถูกเลือกอย่างไร?",
      a: "Nunt จะเสนอภาพและทาเลนต์ที่เหมาะสมตามประเภทธุรกิจและวัตถุประสงค์การใช้งานของบริษัท",
    },
    {
      q: "หลังทำสัญญาแล้ว สามารถเริ่มใช้ภาพได้ทันทีไหม?",
      a: "หลังจากยืนยันภาพและเงื่อนไขการใช้งานเรียบร้อยแล้ว บริษัทสามารถนำไปใช้กับเว็บไซต์ โฆษณา และงานผลิตสื่อต่าง ๆ ได้",
    },
    {
      q: "สามารถติดต่อเพื่อปรึกษาเบื้องต้นอย่างเดียวได้ไหม?",
      a: "ได้ แม้ยังไม่ได้กำหนดรูปแบบการใช้งานที่ชัดเจน ก็สามารถติดต่อเราเพื่อปรึกษาได้",
    },
  ] : isEn ? [
    {
      q: "How much does it cost?",
      a: "Plans start from 50,000 THB. The price may vary depending on the talent, industry, and usage period. Please feel free to contact us for details.",
    },
    {
      q: "Is there a minimum contract period?",
      a: "The minimum usage period is 3 months. Traditional talent contracts often require annual agreements, which can become a large cost even if the monthly amount looks reasonable. We also offer special plans for first-time companies, so please feel free to contact us.",
    },
    {
      q: "Can the visuals be used for advertising and social media?",
      a: "Yes. The visuals can be used for websites, social media posts, ad banners, and other agreed uses within the contract scope.",
    },
    {
      q: "How are the available visuals and talents selected?",
      a: "Nunt suggests suitable visual materials and talent candidates based on your industry and intended use.",
    },
    {
      q: "Can we start using the materials immediately after signing?",
      a: "You can start using the visuals for websites, advertising, and other production work after the materials and usage conditions are confirmed.",
    },
    {
      q: "Can we contact you just for an initial consultation?",
      a: "Yes. You can contact us even if your specific use case has not been decided yet.",
    },
  ] : [
    {
      q: "費用はどのくらいですか？",
      a: "最安5万THB〜ご提供させていただきます。また、タレントごとに金額が異なり、業種や利用期間によって金額が変動する場合があります。まずはお気軽にご相談ください。",
    },
    {
      q: "最低利用期間はありますか？",
      a: "最短3ヶ月からご利用いただけます。通常、タレント起用では年間契約を求められるケースも多く、月額では安く見えても年間では大きな負担になる場合があります。初めての企業様向けのお得なプランもご用意しておりますので、お気軽にご相談ください。",
    },
    {
      q: "広告やSNSにも利用できますか？",
      a: "はい。Webサイト、SNS投稿、広告バナーなど、契約で定めた範囲内でご利用いただけます。",
    },
    {
      q: "利用できる素材やタレントはどのように決まりますか？",
      a: "企業の業種や利用目的に合わせて、Nunt側で利用しやすい素材や候補をご提案します。",
    },
    {
      q: "契約後、すぐに素材を使い始められますか？",
      a: "素材と利用条件の確認が完了した後、Webサイトや広告制作などに活用いただけます。",
    },
    {
      q: "まずは相談だけでも可能ですか？",
      a: "はい。具体的な利用イメージが決まっていない段階でも、お気軽にご相談いただけます。",
    },
  ];

  const reasonItems = isTh ? [
    {
      number: "REASON 01",
      titleLines: ["เปลี่ยนภาพลักษณ์โฆษณา", "และช่วยเพิ่มผลลัพธ์"],
      bodyLines: [
        "การใช้ภาพทาเลนต์ช่วยเพิ่มความโดดเด่นและความน่าเชื่อถือให้กับโฆษณาและเว็บไซต์",
        "เมื่อภาพแรกดูน่าสนใจขึ้น ก็ช่วยเพิ่มโอกาสในการคลิกและการตอบสนองได้",
      ],
      image: reason01,
      alt: "ตัวอย่างภาพทาเลนต์สำหรับแคมเปญโฆษณาความงาม",
    },
    {
      number: "REASON 02",
      titleLines: ["ลดเวลาเตรียมภาพสำหรับการตลาด"],
      bodyLines: [
        "ไม่จำเป็นต้องเริ่มจากการถ่ายภาพหรือแคสติ้งใหม่",
        "ภาพที่พร้อมใช้งานช่วยให้ทีมเดินหน้าจากการตรวจสอบภายในไปสู่การผลิตได้ราบรื่นขึ้น",
      ],
      image: reason02,
      alt: "ตัวอย่างการใช้ภาพทาเลนต์กับผู้หญิงและคอมพิวเตอร์",
      reverse: true,
    },
    {
      number: "REASON 03",
      titleLines: ["ปรับใช้ได้ง่ายกับหลายสื่อ"],
      bodyLines: [
        "ภาพทาเลนต์สามารถนำไปใช้กับเว็บไซต์ โพสต์โซเชียล แบนเนอร์โฆษณา และสื่อรูปแบบอื่นได้",
        "จึงนำไปปรับใช้กับแบรนด์และแคมเปญจริงได้ง่าย",
      ],
      image: reason03Preview,
      alt: "ตัวอย่างการใช้ภาพทาเลนต์กับหลายสื่อ",
      imageFit: "contain",
    },
  ] : isEn ? [
    {
      number: "REASON 01",
      titleLines: ["Improve advertising", "impressions and performance"],
      bodyLines: [
        "Using talent visuals can add visibility and trust to your ads and website.",
        "By improving the first impression, they can also help increase clicks and responses.",
      ],
      image: reason01,
      alt: "Talent visual example for beauty advertising campaign",
    },
    {
      number: "REASON 02",
      titleLines: ["Reduce preparation time"],
      bodyLines: [
        "You do not need to start from photo shoots or casting.",
        "Ready-to-use visuals help your team move smoothly from review to production.",
      ],
      image: reason02,
      alt: "Visual material usage with woman and computer",
      reverse: true,
    },
    {
      number: "REASON 03",
      titleLines: ["Easy to adapt across", "different media"],
      bodyLines: [
        "Talent visuals can be used across websites, social media posts, ad banners, and other formats.",
        "They are easy to apply to real marketing work based on your brand and campaign needs.",
      ],
      image: reason03Preview,
      alt: "Multi-media layout example for talent visual usage",
      imageFit: "contain",
    },
  ] : [
    {
      number: "REASON 01",
      titleLines: ["広告の印象を変え、", "成果につなげる"],
      bodyLines: [
        "タレント素材を活用することで、",
        "広告やWebサイトに視認性と信頼感を加えられます。",
        "第一印象を整え、クリック率や反応改善にもつながります。",
      ],
      image: reason01,
      alt: "美容広告クリエイティブのタレント素材例",
    },
    {
      number: "REASON 02",
      titleLines: ["素材準備の手間を", "減らせる"],
      bodyLines: [
        "撮影やキャスティングから始める必要がなく、",
        "使いやすい素材をそのまま活用できます。",
        "社内確認から制作着手まで、スムーズに進めやすくなります。",
      ],
      image: reason02,
      alt: "女性とPCを使った素材活用イメージ",
      reverse: true,
    },
    {
      number: "REASON 03",
      titleLines: ["媒体に合わせて、", "使い分けやすい"],
      bodyLines: [
        "Webサイト、SNS投稿、広告バナーなど、",
        "用途に合わせて展開しやすいのも特長です。",
        "ブランドや施策に合わせて、実務に落とし込みやすくなります。",
      ],
      image: reason03Preview,
      alt: "媒体展開図の素材活用イメージ",
      imageFit: "contain",
    },
  ];

  return (
    <div style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#001A3D", background: "#fff" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .hero-pc { display: flex; }
        .hero-sp { display: none; }

        .preview-header {
          position: sticky;
          top: 0;
          z-index: 100;
          height: 88px;
          background: rgba(255, 255, 255, 0.94);
          border-bottom: 1px solid rgba(16, 30, 75, 0.08);
          box-shadow: 0 14px 36px rgba(33, 26, 82, 0.05);
          backdrop-filter: blur(16px);
        }

        .preview-header-inner {
          width: min(1200px, calc(100% - 48px));
          height: 100%;
          margin: 0 auto;
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center;
          gap: 34px;
        }

        .preview-logo {
          height: 68px;
          width: auto;
          display: block;
          align-self: center;
        }

        .preview-nav {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 34px;
          height: 100%;
          min-width: 0;
        }

        .preview-nav a {
          color: #061845;
          font-size: 14px;
          font-weight: 600;
          text-decoration: none;
          line-height: 1;
          white-space: nowrap;
        }

        .preview-header-actions {
          display: flex;
          align-items: center;
          gap: 14px;
          height: 100%;
        }

        .preview-btn {
          min-height: 48px;
          padding: 0 22px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 14px;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          border: 1px solid transparent;
          line-height: 1;
          transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
        }

        .preview-btn svg {
          flex: 0 0 auto;
        }

        .preview-btn:hover {
          transform: translateY(-1px);
        }

        .preview-btn-outline {
          color: #061845;
          background: #fff;
          border-color: #061845;
          box-shadow: 0 4px 12px rgba(6, 24, 69, 0.05);
        }

        .preview-btn-gold {
          color: #fff;
          background: linear-gradient(135deg, #c77b0a 0%, #e0a127 48%, #bf7107 100%);
          box-shadow: 0 6px 14px rgba(192, 113, 7, 0.13);
        }

        .preview-hero {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(circle at 78% 18%, rgba(128, 104, 220, 0.2) 0, rgba(128, 104, 220, 0) 34%),
            linear-gradient(132deg, #ffffff 0%, #fbfbff 45%, #f0f1ff 100%);
          padding: 44px 0 28px;
          min-height: calc(100svh - 88px);
          align-items: center;
          isolation: isolate;
        }

        .preview-hero::before {
          content: "";
          position: absolute;
          inset: 0;
          background:
            linear-gradient(142deg, transparent 0 62%, rgba(255,255,255,0.54) 62.2% 66%, transparent 66.2%),
            linear-gradient(150deg, transparent 0 72%, rgba(116, 105, 216, 0.08) 72.2% 76%, transparent 76.2%);
          pointer-events: none;
          z-index: -1;
        }

        .preview-hero-inner {
          width: min(1180px, calc(100% - 64px));
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(540px, 0.96fr) minmax(560px, 1.04fr);
          gap: 54px;
          align-items: center;
        }

        .preview-hero-copy {
          min-width: 0;
        }

        .preview-eyebrow {
          width: fit-content;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 36px;
          padding: 0 20px 1px;
          border-radius: 999px;
          color: #6456de;
          background: rgba(246, 244, 255, 0.88);
          border: 1px solid rgba(116, 105, 216, 0.34);
          font-size: 13px;
          font-weight: 600;
          line-height: 1.2;
          box-shadow: 0 10px 28px rgba(84, 72, 178, 0.08);
        }

        .preview-hero-title {
          margin: 30px 0 0;
          color: #061845;
          font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Sans JP", serif;
          font-size: 44px;
          font-weight: 600;
          line-height: 1.2;
          letter-spacing: 0;
        }

        .preview-mobile-break {
          display: none;
        }

        .preview-hero-subcopy {
          margin: 22px 0 0;
          color: #061845;
          font-size: 16px;
          font-weight: 500;
          line-height: 1.78;
        }

        .preview-cta-row {
          margin-top: 26px;
          display: grid;
          grid-template-columns: minmax(220px, 1.05fr) minmax(200px, 0.95fr);
          gap: 16px;
          max-width: 500px;
        }

        .preview-hero-cta {
          min-height: 48px;
          padding: 0 20px;
          border-radius: 8px;
          justify-content: center;
          gap: 18px;
        }

        .preview-hero-cta > span {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          line-height: 1;
        }

        .preview-support-link {
          margin-top: 20px;
          width: fit-content;
          min-height: 52px;
          padding: 10px 16px;
          display: inline-flex;
          align-items: center;
          gap: 14px;
          border-radius: 10px;
          color: #061845;
          text-decoration: none;
          background: rgba(246, 244, 255, 0.82);
          border: 1px solid rgba(116, 105, 216, 0.12);
          box-shadow: 0 10px 24px rgba(71, 65, 150, 0.05);
        }

        .preview-support-link strong {
          display: block;
          color: #6456de;
          font-size: 15px;
          font-weight: 600;
          line-height: 1.2;
        }

        .preview-support-link span {
          display: block;
          margin-top: 4px;
          color: #061845;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.2;
        }

        .preview-support-arrow {
          margin-left: 8px;
          color: #6456de;
          flex: 0 0 auto;
        }

        .preview-benefits {
          margin-top: 22px;
          padding-top: 16px;
          border-top: 1px solid rgba(6, 24, 69, 0.14);
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          max-width: 570px;
        }

        .preview-benefit {
          min-width: 0;
          padding: 0 12px;
          color: #061845;
          text-align: center;
          border-left: 1px solid rgba(6, 24, 69, 0.14);
          display: grid;
          grid-template-rows: 34px 40px;
          justify-items: center;
          align-items: center;
          row-gap: 8px;
        }

        .preview-benefit:first-child {
          border-left: none;
        }

        .preview-benefit svg {
          width: 34px;
          height: 34px;
          align-self: center;
          justify-self: center;
          color: #6456de;
          margin-bottom: 0;
        }

        .preview-benefit strong {
          min-height: 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.45;
          word-break: keep-all;
          overflow-wrap: normal;
        }

        .preview-benefit-line {
          display: block;
          white-space: nowrap;
        }

        .preview-visual-grid {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: 22px;
          align-items: stretch;
        }

        .preview-card {
          position: relative;
          overflow: hidden;
          min-width: 0;
          border-radius: 22px;
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(116, 105, 216, 0.12);
          box-shadow: 0 14px 34px rgba(32, 28, 84, 0.08);
        }

        .preview-card-female,
        .preview-card-scene,
        .preview-card-male {
          grid-column: span 2;
          min-height: 226px;
        }

        .preview-card-flow,
        .preview-card-corporate {
          grid-column: span 3;
          min-height: 196px;
        }

        .preview-card-audience {
          grid-column: 1 / -1;
          min-height: 72px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          overflow: visible;
          border-radius: 18px;
        }

        .preview-image-card img,
        .preview-corporate-image {
          width: 100%;
          display: block;
          object-fit: cover;
        }

        .preview-model-image {
          height: 128px;
        }

        .preview-model-body,
        .preview-card-body {
          padding: 16px 18px 15px;
        }

        .preview-image-card .preview-model-body,
        .preview-card-corporate .preview-card-body {
          display: flex;
          flex-direction: column;
        }

        .preview-image-card .preview-model-body {
          min-height: 98px;
        }

        .preview-card-corporate .preview-card-body {
          min-height: 108px;
        }

        .preview-card-kicker {
          color: #061845;
          font-size: 12px;
          font-weight: 600;
          line-height: 1.25;
        }

        .preview-model-title {
          margin-top: 8px;
          color: #061845;
          font-size: 14px;
          font-weight: 600;
          line-height: 1.55;
          overflow-wrap: anywhere;
        }

        .preview-card-male .preview-model-title {
          font-size: 13px;
          line-height: 1.55;
          word-break: keep-all;
          overflow-wrap: normal;
        }

        .preview-model-title-line {
          display: block;
          white-space: nowrap;
        }

        .preview-card-progress {
          display: flex;
          gap: 5px;
          width: 56px;
          height: 4px;
          margin-top: 11px;
          align-items: center;
        }

        .preview-image-card .preview-card-progress,
        .preview-card-corporate .preview-card-progress {
          margin-top: auto;
        }

        .preview-card-progress span {
          width: 12px;
          height: 4px;
          border-radius: 999px;
          background: #d9d5fb;
        }

        .preview-card-progress span:first-child {
          width: 22px;
          background: #6456de;
        }

        .preview-card-heading {
          color: #061845;
          font-size: 15px;
          font-weight: 600;
          line-height: 1.2;
          overflow-wrap: anywhere;
        }

        .preview-scene-list,
        .preview-flow-list {
          margin-top: 13px;
          display: grid;
          gap: 0;
        }

        .preview-scene-item,
        .preview-flow-item {
          display: flex;
          align-items: center;
          gap: 12px;
          min-height: 40px;
          border-top: 1px solid rgba(6, 24, 69, 0.09);
          color: #061845;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.3;
        }

        .preview-scene-icon,
        .preview-flow-number {
          width: 28px;
          height: 28px;
          border-radius: 9px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
        }

        .preview-scene-icon {
          color: #6456de;
          background: #f0edff;
        }

        .preview-flow-number {
          color: #fff;
          background: linear-gradient(135deg, #7665ec, #5144c7);
          font-size: 12px;
          font-weight: 700;
          box-shadow: 0 8px 14px rgba(81, 68, 199, 0.16);
        }

        .preview-card-note {
          margin-top: 11px;
          color: #061845;
          font-size: 12px;
          font-weight: 600;
          line-height: 1.65;
        }

        .preview-corporate-image {
          height: 88px;
        }

        .preview-corporate-copy {
          margin-top: 9px;
          color: #061845;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.65;
        }

        .preview-audience-link {
          min-width: 0;
          padding: 16px 24px;
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center;
          gap: 16px;
          color: #061845;
          text-decoration: none;
          background: rgba(255, 255, 255, 0.92);
          border-radius: 18px;
        }

        .preview-audience-link + .preview-audience-link {
          border-left: 1px solid rgba(6, 24, 69, 0.12);
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }

        .preview-audience-link:first-child {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        .preview-audience-link svg {
          color: #6456de;
          align-self: center;
        }

        .preview-audience-link strong {
          display: block;
          font-size: 15px;
          font-weight: 600;
          line-height: 1.25;
        }

        .preview-audience-heading-nowrap {
          white-space: nowrap;
          word-break: keep-all;
          font-size: 14px;
          letter-spacing: 0;
        }

        .preview-audience-link span {
          display: block;
          min-width: 0;
          margin-top: 6px;
          color: #37436a;
          font-size: 12px;
          font-weight: 600;
          line-height: 1.45;
        }

        .preview-audience-link > span {
          display: grid;
          align-content: center;
          gap: 5px;
          margin-top: 0;
        }

        .preview-audience-link > span > span {
          margin-top: 0;
        }

        .preview-mobile-header {
          display: none;
        }

        .about-preview-section {
          position: relative;
          overflow: hidden;
          padding: 96px 0 88px;
          background: #fff;
        }

        .about-preview-inner {
          position: relative;
          z-index: 1;
          width: min(1280px, calc(100% - 128px));
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 0.44fr) minmax(0, 0.56fr);
          gap: 68px;
          align-items: start;
        }

        .about-preview-copy {
          min-width: 0;
          padding-top: 6px;
        }

        .about-section-label {
          display: inline-flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 14px;
          color: #061845;
          font-size: 19px;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 30px;
        }

        .about-section-label::after {
          content: "";
          width: 42px;
          height: 3px;
          border-radius: 999px;
          background: #bab3ff;
        }

        .about-main-title {
          margin: 0;
          color: #061845;
          font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Sans JP", serif;
          font-size: clamp(48px, 3.4vw, 52px);
          font-weight: 700;
          line-height: 1.25;
          letter-spacing: -0.02em;
        }

        .about-main-title .gold {
          color: #d88409;
        }

        .about-title-line {
          white-space: nowrap;
        }

        .about-title-service-sp {
          display: none;
        }

        .about-title-service-pc {
          font-size: 0.9em;
        }

        /* タイ語PC版：長い見出しが右カラムに被らないよう折り返しを許可 */
        .about-main-title-th {
          font-size: clamp(36px, 2.6vw, 44px);
        }
        .about-main-title-th .about-title-line {
          white-space: normal;
        }

        /* タイ語版：PC専用調整の手前では既存のコンパクトな比率を維持 */
        .about-preview-inner-th {
          grid-template-columns: minmax(0, 0.47fr) minmax(0, 0.53fr);
          gap: 56px;
        }
        .about-preview-inner-th .about-compare-card {
          max-width: 490px;
          margin-left: auto;
          margin-right: auto;
        }

        /* タイ語PC版：右比較カードの幅と内部カラムを確保 */
        @media (min-width: 981px) {
          .about-preview-inner-en .about-compare-card {
            transform: translateX(-18px);
          }
          .about-preview-inner-th {
            grid-template-columns: minmax(0, 0.43fr) minmax(600px, 0.57fr);
            gap: 56px;
          }
          .about-preview-inner-th .about-compare-card {
            width: 100%;
            max-width: none;
            margin-left: 0;
            margin-right: 0;
            padding: 34px;
          }
          .about-preview-inner-th .about-compare-grid {
            grid-template-columns: minmax(0, 1fr) 30px minmax(276px, 1.06fr);
            gap: 20px;
          }
          .about-preview-inner-th .about-compare-column-nunt {
            padding-left: 20px;
          }
          .about-preview-inner-th .about-compare-heading {
            margin-bottom: 24px;
            font-size: 22px;
          }
          .about-preview-inner-th .about-compare-column-nunt .about-compare-heading {
            font-size: 25px;
            margin-bottom: 26px;
          }
          .about-preview-inner-th .about-compare-item {
            min-height: 78px;
            padding-left: 18px;
            padding-right: 18px;
            gap: 16px;
            font-size: 18px;
          }
          .about-preview-inner-th .about-compare-item span {
            white-space: nowrap;
            word-break: keep-all;
          }
          .about-preview-inner-th .about-compare-item svg {
            width: 30px;
            height: 30px;
          }
          .about-preview-inner-th .about-nunt-item {
            min-height: 104px;
            padding-left: 20px;
            padding-right: 20px;
            gap: 16px;
          }
          .about-preview-inner-th .about-nunt-item svg {
            width: 34px;
            height: 34px;
          }
          .about-preview-inner-th .about-nunt-label {
            font-size: 21px;
            white-space: nowrap;
          }
          .about-preview-inner-th .about-zero-item {
            min-height: 132px;
          }
          .about-preview-inner-th .about-zero-number {
            font-size: 56px;
          }
          .about-preview-inner-th .about-zero-currency {
            font-size: 27px;
          }
        }

        .about-main-copy {
          margin: 32px 0 0;
          color: #061845;
          font-size: 18px;
          font-weight: 500;
          line-height: 1.86;
          max-width: 520px;
        }

        .about-copy-spacer {
          display: block;
          height: 14px;
        }

        .about-download-button {
          margin-top: 34px;
          min-height: 58px;
          width: min(100%, 386px);
          padding: 0 24px;
          border-radius: 17px;
          display: inline-grid;
          grid-template-columns: 24px 1fr 24px;
          align-items: center;
          justify-items: center;
          column-gap: 12px;
          color: #fff;
          background: linear-gradient(135deg, #c77b0a 0%, #e0a127 52%, #bf7107 100%);
          box-shadow: 0 8px 18px rgba(192, 113, 7, 0.12);
          text-decoration: none;
          font-size: 17px;
          font-weight: 700;
          line-height: 1;
          transition: transform 0.18s ease, filter 0.18s ease, box-shadow 0.18s ease;
        }

        .about-download-button:hover {
          transform: translateY(-1px);
          filter: brightness(0.96);
          box-shadow: 0 10px 20px rgba(192, 113, 7, 0.15);
        }

        .about-download-button > span {
          line-height: 1;
          text-align: center;
        }

        .about-compare-card {
          min-width: 0;
          margin-top: 22px;
          padding: 40px;
          border-radius: 30px;
          background: rgba(255, 255, 255, 0.96);
          border: 1px solid #e8e8f4;
          box-shadow: 0 16px 36px rgba(32, 28, 84, 0.07);
        }

        .about-compare-grid {
          display: grid;
          grid-template-columns: minmax(0, 0.95fr) 34px minmax(0, 1.05fr);
          gap: 24px;
          align-items: center;
        }

        .about-compare-column {
          min-width: 0;
        }

        .about-compare-column-nunt {
          position: relative;
          padding-left: 24px;
          border-left: 1px dashed rgba(116, 105, 216, 0.28);
        }

        .about-compare-heading {
          margin: 0 0 26px;
          color: #061845;
          font-size: 24px;
          font-weight: 700;
          line-height: 1.25;
          text-align: center;
        }

        .about-compare-column-nunt .about-compare-heading {
          font-size: 26px;
          margin-bottom: 28px;
        }

        .about-compare-list {
          display: grid;
          gap: 14px;
        }

        .about-compare-item {
          min-height: 84px;
          padding: 0 22px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          gap: 18px;
          color: #1d2a51;
          background: rgba(248, 249, 253, 0.74);
          border: 1px solid rgba(232, 232, 244, 0.58);
          font-size: 19px;
          font-weight: 650;
          line-height: 1.25;
        }

        .about-compare-item svg {
          width: 32px;
          height: 32px;
          color: #2c365b;
          flex: 0 0 auto;
        }

        .about-compare-arrow {
          width: 34px;
          height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: rgba(116, 105, 216, 0.18);
          transform: translateY(26px);
        }

        .about-arrow-note {
          display: none;
        }

        .about-nunt-list {
          display: grid;
          gap: 19px;
        }

        .about-nunt-item {
          min-height: 112px;
          width: 100%;
          padding: 0 28px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          gap: 22px;
          color: #061845;
          background: linear-gradient(135deg, #ffffff 0%, #fbfbff 100%);
          border: 1px solid rgba(116, 105, 216, 0.28);
          box-shadow: 0 14px 30px rgba(116, 105, 216, 0.08);
        }

        .about-nunt-item svg {
          width: 40px;
          height: 40px;
          color: #6456de;
          flex: 0 0 auto;
        }

        .about-nunt-label {
          display: block;
          font-size: 23px;
          font-weight: 700;
          line-height: 1.25;
          white-space: nowrap;
        }

        .about-zero-item {
          min-height: 142px;
          background: linear-gradient(135deg, #ffffff 0%, #fffaf1 100%);
          border-color: rgba(216, 132, 9, 0.34);
          box-shadow: 0 18px 34px rgba(216, 132, 9, 0.12);
        }

        .about-zero-item svg {
          color: #6b5df0;
        }

        .about-zero-copy {
          display: grid;
          gap: 4px;
        }

        .about-zero-value {
          display: flex;
          align-items: baseline;
          gap: 8px;
          color: #d88409;
          line-height: 1;
          white-space: nowrap;
        }

        .about-zero-number {
          font-size: 60px;
          font-weight: 800;
          letter-spacing: 0;
        }

        .about-zero-currency {
          font-size: 29px;
          font-weight: 800;
        }

        .partnership-section {
          position: relative;
          overflow: hidden;
          padding: 56px 0 58px;
          background:
            radial-gradient(circle at 16% 18%, rgba(116, 105, 216, 0.16) 0, rgba(116, 105, 216, 0) 32%),
            radial-gradient(circle at 86% 72%, rgba(216, 132, 9, 0.08) 0, rgba(216, 132, 9, 0) 28%),
            linear-gradient(135deg, #fbfbff 0%, #f3f4ff 48%, #eef2fb 100%);
        }

        .partnership-inner {
          width: min(860px, calc(100% - 64px));
          margin: 0 auto;
        }

        .partnership-header {
          max-width: 820px;
          margin: 0 auto 24px;
          text-align: center;
        }

        .partnership-label {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          min-height: 34px;
          padding: 0 16px;
          border-radius: 999px;
          color: #6456de;
          background: rgba(255, 255, 255, 0.72);
          border: 1px solid rgba(116, 105, 216, 0.18);
          font-size: 12px;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0.04em;
        }

        .partnership-title {
          margin: 24px 0 0;
          color: #061845;
          font-size: clamp(34px, 3vw, 44px);
          font-weight: 750;
          line-height: 1.34;
          letter-spacing: 0;
        }

        .partnership-lead {
          margin: 20px auto 0;
          max-width: 720px;
          color: #172653;
          font-size: 16px;
          font-weight: 500;
          line-height: 1.92;
          text-align: center;
        }

        .partnership-lead-pc {
          display: contents;
        }

        .partnership-lead-sp {
          display: none;
        }

        .pf-diagram {
          display: grid;
          grid-template-columns: minmax(190px, 0.82fr) 30px minmax(305px, 1.36fr) 30px minmax(190px, 0.82fr);
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: 860px;
          margin: 0 auto;
        }

        .pf-card {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          min-width: 0;
          text-align: center;
          background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 255, 255, 0.9) 100%);
          box-shadow: 0 24px 62px rgba(20, 25, 80, 0.1);
          backdrop-filter: blur(10px);
        }

        .pf-card-side {
          min-height: 322px;
          padding: 16px 15px 18px;
          border: 1px solid rgba(116, 105, 216, 0.22);
          border-radius: 24px;
        }

        .pf-card-nunt {
          min-height: 388px;
          padding: 21px 23px 23px;
          border: 1px solid rgba(201, 168, 76, 0.72);
          border-radius: 28px;
          box-shadow:
            0 30px 76px rgba(20, 25, 80, 0.14),
            0 0 0 6px rgba(255, 255, 255, 0.28);
          z-index: 1;
        }

        .pf-card-heading {
          width: 100%;
          min-height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 12px;
          border-radius: 999px;
          background: linear-gradient(135deg, rgba(229, 225, 250, 0.96), rgba(246, 244, 255, 0.92));
          color: #061845;
          font-size: clamp(17px, 1.25vw, 19px);
          font-weight: 800;
          line-height: 1.22;
          white-space: nowrap;
        }

        .pf-primary-icon {
          width: 100px;
          height: 100px;
          margin: 14px auto 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          color: #2f3298;
          background:
            radial-gradient(circle at 34% 24%, rgba(255, 255, 255, 0.8), rgba(236, 233, 252, 0.92) 68%),
            #efedfb;
        }

        .pf-primary-icon svg {
          width: 60px;
          height: 60px;
        }

        .pf-card-divider {
          width: 100%;
          height: 1px;
          margin: 0 0 12px;
          background: linear-gradient(90deg, rgba(116, 105, 216, 0), rgba(116, 105, 216, 0.28), rgba(116, 105, 216, 0));
        }

        .pf-item-list {
          width: 100%;
          display: grid;
          gap: 9px;
        }

        .pf-item,
        .pf-feature {
          display: flex;
          align-items: center;
          min-width: 0;
          border: 1px solid rgba(116, 105, 216, 0.12);
          background:
            linear-gradient(135deg, rgba(255, 255, 255, 0.94), rgba(246, 245, 253, 0.92));
          box-shadow: 0 12px 26px rgba(28, 31, 92, 0.055);
        }

        .pf-item {
          gap: 10px;
          min-height: 48px;
          padding: 6px 10px;
          border-radius: 15px;
          color: #071946;
          font-size: 15px;
          font-weight: 800;
          line-height: 1.36;
          text-align: left;
        }

        .pf-item-icon,
        .pf-feature-icon {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          color: #2f3298;
          background: rgba(236, 233, 252, 0.94);
        }

        .pf-item-icon {
          width: 33px;
          height: 33px;
        }

        .pf-flow-arrow {
          display: grid;
          justify-items: center;
          align-content: center;
          color: #061845;
        }

        .pf-arr-h {
          width: 30px;
          height: 14px;
        }

        .pf-arr-v {
          display: none;
          width: 14px;
          height: 32px;
        }

        .pf-arrow-line,
        .pf-arrow-head {
          stroke: currentColor;
          stroke-width: 1.75;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        .pf-flow-arrow:nth-of-type(2) {
          color: #b88f24;
        }

        .pf-nunt-logo {
          height: 56px;
          width: auto;
          display: block;
          margin: 0 auto;
          flex-shrink: 0;
        }

        .pf-gold-rule {
          width: 100%;
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          gap: 12px;
          margin: 8px 0 16px;
          color: #c9a84c;
        }

        .pf-gold-rule::before,
        .pf-gold-rule::after {
          content: "";
          height: 1px;
          background: linear-gradient(90deg, rgba(201, 168, 76, 0), rgba(201, 168, 76, 0.7));
        }

        .pf-gold-rule::after {
          background: linear-gradient(90deg, rgba(201, 168, 76, 0.7), rgba(201, 168, 76, 0));
        }

        .pf-gold-star {
          width: 8px;
          height: 8px;
          border-radius: 2px;
          background: currentColor;
          transform: rotate(45deg);
        }

        .pf-nunt-copy {
          color: #061845;
          font-size: clamp(26px, 2vw, 31px);
          font-weight: 850;
          line-height: 1.3;
          letter-spacing: 0;
          text-align: center;
        }

        .pf-nunt-subrule {
          width: 100%;
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          gap: 12px;
          margin: 16px 0 12px;
          color: rgba(201, 168, 76, 0.9);
        }

        .pf-nunt-subrule::before,
        .pf-nunt-subrule::after {
          content: "";
          height: 1px;
          background: rgba(116, 105, 216, 0.16);
        }

        .pf-nunt-dot {
          width: 8px;
          height: 8px;
          border-radius: 2px;
          background: currentColor;
          transform: rotate(45deg);
        }

        .pf-feature-grid {
          width: 100%;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 9px;
        }

        .pf-feature {
          gap: 9px;
          min-height: 56px;
          padding: 8px 9px;
          border-radius: 14px;
          color: #071946;
          font-size: 14px;
          font-weight: 800;
          line-height: 1.32;
          text-align: left;
        }

        .pf-feature-icon {
          width: 33px;
          height: 33px;
        }

        .preview-reasons-section {
          padding: 60px 0 64px;
          background: #fff;
        }

        .preview-reasons-inner {
          width: min(1040px, calc(100% - 64px));
          margin: 0 auto;
        }

        .preview-reasons-header {
          max-width: 740px;
          margin: 0 auto 36px;
          text-align: center;
        }

        .preview-reasons-title {
          margin: 0;
          color: #061845;
          font-size: 32px;
          font-weight: 900;
          line-height: 1.35;
          letter-spacing: 0;
        }

        .preview-reasons-lead {
          margin: 18px auto 0;
          color: #253154;
          font-size: 15.5px;
          font-weight: 500;
          line-height: 1.9;
        }

        .usecase-section {
          position: relative;
          overflow: hidden;
          padding: 56px 0 60px;
          background:
            radial-gradient(circle at 16% 18%, rgba(116, 105, 216, 0.16) 0, rgba(116, 105, 216, 0) 32%),
            radial-gradient(circle at 86% 72%, rgba(216, 132, 9, 0.08) 0, rgba(216, 132, 9, 0) 28%),
            linear-gradient(135deg, #fbfbff 0%, #f3f4ff 48%, #eef2fb 100%);
        }

        .usecase-inner {
          width: min(1100px, calc(100% - 40px));
          margin: 0 auto;
        }

        .usecase-header {
          max-width: 740px;
          margin: 0 auto 34px;
          text-align: center;
        }

        .usecase-title {
          font-family: inherit;
          text-rendering: optimizeLegibility;
          -webkit-font-smoothing: antialiased;
        }

        .usecase-lead {
          margin-top: 14px;
        }

        .usecase-chip {
          display: inline-flex;
          align-items: center;
          min-height: 24px;
          padding: 0 8px;
          border-radius: 999px;
          background: rgba(249, 248, 255, 0.68);
          border: 1px solid rgba(116, 105, 216, 0.1);
          color: #566079;
          font-size: 10px;
          font-weight: 600;
          line-height: 1;
          white-space: nowrap;
        }

        .preview-reasons-list {
          display: grid;
          gap: 24px;
        }

        .preview-reason-card {
          position: relative;
          display: grid;
          grid-template-columns: minmax(0, 0.98fr) minmax(330px, 0.72fr);
          gap: 30px;
          align-items: center;
          min-height: 252px;
          padding: 24px;
          border-radius: 24px;
          background:
            linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 249, 255, 0.92) 100%);
          border: 1px solid rgba(116, 105, 216, 0.16);
          box-shadow: 0 16px 38px rgba(32, 28, 84, 0.07);
          overflow: hidden;
        }

        .preview-reason-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, rgba(201, 168, 76, 0.08), rgba(201, 168, 76, 0) 36%);
          pointer-events: none;
        }

        .preview-reason-card.is-reverse {
          grid-template-columns: minmax(330px, 0.72fr) minmax(0, 0.98fr);
        }

        .preview-reason-card.is-reverse::before {
          background: linear-gradient(270deg, rgba(201, 168, 76, 0.08), rgba(201, 168, 76, 0) 36%);
        }

        .preview-reason-card.is-reverse .preview-reason-media {
          order: -1;
        }

        .preview-reason-copy,
        .preview-reason-media {
          position: relative;
          z-index: 1;
        }

        .preview-reason-copy {
          display: grid;
          grid-template-columns: 5px minmax(0, 1fr);
          gap: 17px;
          align-items: start;
          padding: 4px 0;
        }

        .preview-reason-line {
          width: 5px;
          min-height: 138px;
          border-radius: 999px;
          background: linear-gradient(180deg, #d8a132 0%, #b88418 100%);
          box-shadow: 0 0 0 5px rgba(216, 132, 9, 0.08);
        }

        .preview-reason-number {
          display: inline-flex;
          align-items: center;
          min-height: 28px;
          padding: 0 12px;
          border-radius: 999px;
          color: #6b5df0;
          background: rgba(246, 244, 255, 0.92);
          border: 1px solid rgba(116, 105, 216, 0.18);
          font-size: 12px;
          font-weight: 800;
          line-height: 1;
          letter-spacing: 0.04em;
        }

        .preview-reason-heading {
          margin: 14px 0 0;
          color: #061845;
          font-size: 23px;
          font-weight: 900;
          line-height: 1.48;
          letter-spacing: 0;
        }

        .preview-reason-heading-line,
        .preview-reason-text-line {
          display: inline;
        }

        .preview-reason-text {
          margin: 14px 0 0;
          color: #37436a;
          font-size: 14.5px;
          font-weight: 500;
          line-height: 1.86;
        }

        .preview-reason-media {
          margin: 0;
          overflow: hidden;
          border-radius: 18px;
          background: #fff;
          border: 1px solid rgba(232, 232, 244, 0.95);
          box-shadow: 0 12px 28px rgba(20, 25, 80, 0.08);
          aspect-ratio: 16 / 11;
        }

        .preview-reason-media img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
        }

        .preview-reason-media.contain img {
          object-fit: cover;
          padding: 0;
          background: #fff;
        }

        .preview-faq-section {
          padding: 64px 0;
          background: #fff;
        }

        .preview-faq-inner {
          width: min(720px, calc(100% - 40px));
          margin: 0 auto;
        }

        .preview-faq-label-wrap {
          text-align: center;
          margin-bottom: 10px;
        }

        .preview-faq-label {
          color: #b88418;
          font-size: 11px;
          font-weight: 800;
          line-height: 1;
        }

        .preview-faq-title {
          margin: 0 0 34px;
          color: #001A3D;
          text-align: center;
          font-size: 26px;
          font-weight: 900;
          line-height: 1.45;
        }

        .preview-faq-list {
          display: grid;
          gap: 12px;
        }

        .preview-faq-item {
          overflow: hidden;
          border-radius: 8px;
          background: #fff;
          border: 1px solid rgba(6, 24, 69, 0.1);
          box-shadow: 0 8px 22px rgba(32, 28, 84, 0.04);
          transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
        }

        .preview-faq-item.is-open {
          background: linear-gradient(180deg, #fff 0%, #fbfaff 100%);
          border-color: rgba(116, 105, 216, 0.24);
          box-shadow: 0 10px 26px rgba(32, 28, 84, 0.06);
        }

        .preview-faq-button {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 18px;
          background: transparent;
          border: 0;
          color: #001A3D;
          cursor: pointer;
          text-align: left;
        }

        .preview-faq-question {
          flex: 1;
          color: #001A3D;
          font-size: 14.5px;
          font-weight: 800;
          line-height: 1.6;
        }

        .preview-faq-icon {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          color: #061845;
          background: #f6f4ff;
          border: 1px solid rgba(116, 105, 216, 0.22);
          font-size: 17px;
          font-weight: 800;
          line-height: 1;
          transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
        }

        .preview-faq-button:hover .preview-faq-icon {
          color: #b88418;
          background: #fff8ed;
          border-color: rgba(184, 132, 24, 0.35);
        }

        .preview-faq-item.is-open .preview-faq-icon {
          color: #d8a132;
          background: #061845;
          border-color: #061845;
        }

        .preview-faq-answer {
          padding: 0 58px 20px 18px;
          color: #4b587b;
          font-size: 13.5px;
          font-weight: 500;
          line-height: 1.9;
        }

        .preview-partner-section {
          padding: 64px 0 72px;
          background: linear-gradient(180deg, #fff 0%, #fbfaff 100%);
        }

        .preview-partner-card {
          width: min(1100px, calc(100% - 48px));
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(360px, 430px);
          grid-template-areas:
            "copy media"
            "cta media";
          align-items: center;
          gap: 26px 48px;
          padding: 34px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.94);
          border: 1px solid rgba(6, 24, 69, 0.1);
          box-shadow: 0 14px 34px rgba(32, 28, 84, 0.06);
        }

        .preview-partner-copy {
          grid-area: copy;
          min-width: 0;
        }

        .preview-partner-label {
          display: inline-flex;
          align-items: center;
          min-height: 26px;
          padding: 0 11px;
          border-radius: 999px;
          color: #b88418;
          background: #fff8ed;
          border: 1px solid rgba(184, 132, 24, 0.22);
          font-size: 11px;
          font-weight: 800;
          line-height: 1;
          letter-spacing: 0.04em;
        }

        .preview-partner-title {
          margin: 16px 0 0;
          color: #061845;
          font-size: 27px;
          font-weight: 900;
          line-height: 1.45;
          letter-spacing: 0;
        }

        .preview-partner-text {
          margin: 14px 0 0;
          color: #37436a;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.9;
        }

        .preview-partner-chips {
          margin-top: 20px;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .preview-partner-chip {
          display: inline-flex;
          align-items: center;
          min-height: 32px;
          padding: 0 13px;
          border-radius: 999px;
          color: #061845;
          background: #f6f4ff;
          border: 1px solid rgba(116, 105, 216, 0.18);
          font-size: 12.5px;
          font-weight: 700;
          line-height: 1;
        }

        .preview-partner-media {
          grid-area: media;
          margin: 0;
          overflow: hidden;
          border-radius: 8px;
          border: 1px solid rgba(116, 105, 216, 0.12);
          background: #f6f4ff;
          box-shadow: 0 10px 24px rgba(32, 28, 84, 0.06);
          aspect-ratio: 16 / 10;
        }

        .preview-partner-image {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
        }

        .preview-partner-cta {
          grid-area: cta;
          width: fit-content;
          min-height: 44px;
          padding: 0 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          border-radius: 8px;
          color: #061845;
          background: #fff;
          border: 1px solid rgba(6, 24, 69, 0.62);
          box-shadow: 0 6px 14px rgba(6, 24, 69, 0.05);
          font-size: 13.5px;
          font-weight: 800;
          line-height: 1;
          text-decoration: none;
          transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
        }

        .preview-partner-cta:hover {
          color: #b88418;
          background: #fff8ed;
          border-color: rgba(184, 132, 24, 0.55);
          box-shadow: 0 8px 18px rgba(184, 132, 24, 0.12);
          transform: translateY(-1px);
        }

        .preview-partner-cta svg {
          flex: 0 0 auto;
        }

        @media (max-width: 1180px) {
          .preview-header-inner {
            width: min(1120px, calc(100% - 36px));
            gap: 22px;
          }
          .preview-nav { gap: 22px; }
          .preview-btn { padding: 0 18px; font-size: 14px; }
          .preview-hero-inner {
            width: min(1120px, calc(100% - 44px));
            grid-template-columns: minmax(390px, 0.9fr) minmax(500px, 1.1fr);
            gap: 38px;
          }
          .preview-hero-title { font-size: 44px; }
          .about-preview-inner {
            width: min(1120px, calc(100% - 56px));
            gap: 50px;
          }
          .about-main-title {
            font-size: clamp(44px, 3.5vw, 50px);
          }
          .about-compare-card {
            padding: 32px;
          }
          .about-compare-grid {
            grid-template-columns: minmax(0, 0.94fr) 30px minmax(0, 1.06fr);
            gap: 18px;
          }
          .about-compare-column-nunt {
            padding-left: 20px;
          }
          .about-compare-heading {
            font-size: 23px;
          }
          .about-compare-item,
          .about-nunt-item {
            padding-left: 22px;
            padding-right: 22px;
          }
          .about-zero-number {
            font-size: 52px;
          }
          .partnership-inner {
            width: min(840px, calc(100% - 48px));
          }
          .pf-diagram {
            grid-template-columns: minmax(182px, 0.82fr) 26px minmax(294px, 1.36fr) 26px minmax(182px, 0.82fr);
          }
          .pf-card-side {
            min-height: 310px;
            padding: 15px 14px 17px;
          }
          .pf-card-nunt {
            min-height: 372px;
            padding: 19px 20px 21px;
          }
          .pf-card-heading {
            min-height: 38px;
          }
          .pf-primary-icon {
            width: 92px;
            height: 92px;
            margin: 13px auto 13px;
          }
          .pf-primary-icon svg {
            width: 55px;
            height: 55px;
          }
          .pf-item {
            min-height: 46px;
            padding: 6px 9px;
            font-size: 14px;
          }
          .pf-item-icon {
            width: 31px;
            height: 31px;
          }
          .pf-feature {
            min-height: 52px;
            padding: 7px 8px;
            font-size: 13px;
          }
          .preview-reasons-inner {
            width: min(100% - 52px, 980px);
          }
          .preview-reason-card,
          .preview-reason-card.is-reverse {
            grid-template-columns: minmax(0, 1fr) minmax(300px, 0.7fr);
            gap: 26px;
          }
          .preview-reason-card.is-reverse {
            grid-template-columns: minmax(300px, 0.7fr) minmax(0, 1fr);
          }
        }

        @media (max-width: 980px) and (min-width: 769px) {
          .preview-nav { gap: 16px; }
          .preview-nav a { font-size: 12px; }
          .preview-header-actions { gap: 8px; }
          .preview-btn { min-height: 46px; padding: 0 12px; font-size: 12px; }
          .preview-hero-inner {
            grid-template-columns: 1fr;
            max-width: 760px;
          }
          .preview-hero-copy { max-width: 640px; }
          .about-preview-inner {
            grid-template-columns: 1fr;
            max-width: 760px;
          }
          .about-preview-copy {
            max-width: 680px;
          }
          .about-compare-card {
            margin-top: 0;
          }
          .pf-diagram {
            grid-template-columns: minmax(150px, 0.82fr) 22px minmax(240px, 1.36fr) 22px minmax(150px, 0.82fr);
          }
          .pf-card-side {
            min-height: 294px;
            padding: 14px 11px 16px;
          }
          .pf-card-nunt {
            min-height: 354px;
            padding: 18px 16px 20px;
          }
          .pf-card-heading {
            min-height: 38px;
            font-size: 15px;
          }
          .pf-primary-icon {
            width: 84px;
            height: 84px;
            margin: 12px auto 12px;
          }
          .pf-primary-icon svg {
            width: 50px;
            height: 50px;
          }
          .pf-nunt-copy {
            font-size: 24px;
          }
          .pf-item {
            min-height: 42px;
            gap: 7px;
            padding: 5px 7px;
            font-size: 13px;
          }
          .pf-item-icon {
            width: 29px;
            height: 29px;
          }
          .pf-feature {
            min-height: 50px;
            gap: 6px;
            padding: 6px;
            font-size: 12.5px;
          }
          .pf-feature-icon {
            width: 29px;
            height: 29px;
          }
          .preview-reason-card,
          .preview-reason-card.is-reverse {
            grid-template-columns: 1fr;
          }
          .preview-reason-card.is-reverse .preview-reason-media {
            order: 0;
          }
        }

        @media (max-width: 768px) {
          .pc-nav.preview-header { display: none !important; }
          .sp-nav.preview-mobile-header {
            display: flex !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 100 !important;
            height: 76px !important;
            background: rgba(255, 255, 255, 0.96) !important;
            border-bottom: 1px solid rgba(16, 30, 75, 0.08) !important;
            box-shadow: 0 8px 22px rgba(33, 26, 82, 0.05);
            backdrop-filter: blur(14px);
          }
          .preview-mobile-header-inner {
            width: 100%;
            padding: 0 16px;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .preview-mobile-logo {
            width: 104px;
            height: auto;
            display: block;
            margin-right: auto;
            flex: 0 0 auto;
          }
          .preview-mobile-download {
            min-height: 38px;
            padding: 0 12px;
            border-radius: 9px;
            color: #fff;
            background: linear-gradient(135deg, #c77b0a 0%, #e0a127 48%, #bf7107 100%);
            box-shadow: 0 6px 14px rgba(192, 113, 7, 0.12);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            font-size: 12.5px;
            font-weight: 700;
            line-height: 1;
            text-decoration: none;
            white-space: nowrap;
          }
          .preview-mobile-download svg {
            width: 15px;
            height: 15px;
          }
          .preview-menu-button {
            width: 38px;
            height: 38px;
            border: 0;
            background: transparent;
            color: #061845;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            flex: 0 0 auto;
            cursor: pointer;
          }
          .preview-mobile-menu {
            position: fixed;
            top: 76px;
            left: 0;
            right: 0;
            z-index: 99;
            padding: 10px 24px 18px;
            background: rgba(255, 255, 255, 0.98);
            box-shadow: 0 18px 38px rgba(33, 26, 82, 0.12);
          }
          .preview-mobile-menu a {
            display: block;
            padding: 15px 0;
            border-bottom: 1px solid rgba(6, 24, 69, 0.08);
            color: #061845;
            font-size: 15px;
            font-weight: 800;
            text-decoration: none;
          }
          .hero-pc.preview-hero { display: none !important; }
          .hero-sp.preview-mobile-hero {
            display: block !important;
            position: relative;
            overflow: hidden;
            min-height: auto;
            padding: 34px 0 38px !important;
            background:
              radial-gradient(circle at 82% 4%, rgba(128, 104, 220, 0.18) 0, rgba(128, 104, 220, 0) 34%),
              linear-gradient(145deg, #ffffff 0%, #fbfbff 44%, #f0f1ff 100%);
          }
          .preview-mobile-hero::before {
            content: "";
            position: absolute;
            inset: 0;
            background:
              linear-gradient(136deg, transparent 0 54%, rgba(255,255,255,0.5) 54.2% 60%, transparent 60.2%),
              linear-gradient(146deg, transparent 0 68%, rgba(116,105,216,0.08) 68.2% 74%, transparent 74.2%);
            pointer-events: none;
          }
          .preview-hero-inner {
            position: relative;
            z-index: 1;
            width: min(100% - 40px, 520px);
            display: block;
          }
          .preview-eyebrow {
            max-width: 100%;
            min-height: 34px;
            padding: 0 13px 1px;
            font-size: 12px;
            line-height: 1.2;
          }
          .preview-hero-title {
            margin-top: 24px;
            font-size: clamp(29px, 7.6vw, 32px) !important;
            line-height: 1.22;
            overflow-wrap: anywhere;
          }
          .preview-mobile-break {
            display: inline;
          }
          .preview-hero-subcopy {
            margin-top: 18px;
            font-size: 14.5px;
            line-height: 1.72;
          }
          .preview-copy-spacer {
            display: block;
            height: 8px;
          }
          .preview-cta-row {
            max-width: none;
            margin-top: 24px;
            display: grid;
            grid-template-columns: 1fr;
            gap: 9px;
          }
          .preview-hero-cta {
            min-height: 46px;
            width: 100%;
            border-radius: 9px;
            padding: 0 20px;
            font-size: 16px;
          }
          .preview-support-link {
            width: min(100%, 342px);
            margin: 14px auto 0;
            min-height: 50px;
            justify-content: center;
            padding: 8px 16px;
            gap: 12px;
          }
          .preview-support-link strong { font-size: 14px; }
          .preview-support-link span { font-size: 12px; }
          .preview-support-link > svg:first-child {
            width: 28px;
            height: 28px;
          }
          .preview-benefits {
            margin-top: 15px;
            padding-top: 0;
            width: 100%;
            max-width: none;
            grid-template-columns: 1fr;
            gap: 5px;
            justify-content: stretch;
            border-top: 0;
          }
          .preview-benefit {
            width: 100%;
            min-width: 0;
            max-width: 100%;
            min-height: 38px;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            gap: 9px;
            text-align: left;
            border: 1px solid rgba(116, 105, 216, 0.12);
            border-radius: 12px;
            padding: 6px 10px;
            background: rgba(255,255,255,0.74);
          }
          .preview-benefit svg {
            width: 22px;
            height: 22px;
            margin-bottom: 0;
          }
          .preview-benefit strong {
            min-height: 0;
            flex-direction: row;
            justify-content: flex-start;
            gap: 4px;
            font-size: 12px;
            line-height: 1.35;
          }
          .preview-benefit-optional {
            display: none !important;
          }
          .preview-visual-grid {
            margin-top: 24px;
            grid-template-columns: 1fr;
            gap: 12px;
            overflow: hidden;
          }
          .preview-visual-grid > * {
            min-width: 0;
          }
          .preview-card {
            border-radius: 16px;
            box-shadow: 0 10px 26px rgba(32, 28, 84, 0.08);
          }
          .preview-card-female,
          .preview-card-scene,
          .preview-card-flow,
          .preview-card-corporate {
            grid-column: span 1;
            min-height: 0;
          }
          .preview-card-male {
            display: none;
          }
          .preview-model-image {
            height: 128px;
          }
          .preview-card-body,
          .preview-model-body {
            padding: 13px 14px 12px;
          }
          .preview-card-kicker,
          .preview-model-title,
          .preview-card-heading {
            font-size: 14px;
          }
          .preview-scene-item,
          .preview-flow-item {
            min-height: 36px;
            gap: 10px;
            font-size: 12.5px;
          }
          .preview-scene-icon,
          .preview-flow-number {
            width: 26px;
            height: 26px;
          }
          .preview-card-note,
          .preview-corporate-copy {
            font-size: 12px;
          }
          .preview-corporate-image {
            height: 98px;
          }
          .preview-card-corporate .preview-card-body {
            min-height: auto;
            padding: 11px 14px 10px;
          }
          .preview-card-corporate .preview-card-heading {
            font-size: 13px;
            line-height: 1.2;
          }
          .preview-card-corporate .preview-card-progress {
            margin-top: 9px;
          }
          .preview-corporate-copy {
            margin-top: 6px;
            line-height: 1.55;
          }
          .preview-card-audience {
            grid-column: 1 / -1;
            min-height: 0;
            display: grid;
            grid-template-columns: 1fr;
            gap: 14px;
            background: transparent;
            border: 0;
            box-shadow: none;
          }
          .preview-audience-link,
          .preview-audience-link:first-child,
          .preview-audience-link + .preview-audience-link {
            border-radius: 18px;
            border: 1px solid rgba(116, 105, 216, 0.12);
            box-shadow: 0 10px 26px rgba(32, 28, 84, 0.08);
            grid-template-columns: auto 1fr auto;
            padding: 12px 14px;
            gap: 10px;
          }
          .preview-audience-link > svg:first-child {
            width: 32px;
            height: 32px;
          }
          .preview-audience-link > span {
            align-content: center;
            gap: 4px;
          }
          .preview-audience-link strong { font-size: 13.5px; line-height: 1.2; }
          .preview-audience-link span { font-size: 11.5px; line-height: 1.42; }
          #about.about-preview-section {
            padding: 66px 0 72px !important;
          }
          .about-preview-inner {
            width: min(100% - 44px, 520px);
            display: block;
          }
          .about-section-label {
            margin-bottom: 26px;
            gap: 12px;
            font-size: 18px;
          }
          .about-main-title {
            font-size: clamp(31px, 8.8vw, 36px);
            line-height: 1.23;
            letter-spacing: -0.02em;
          }
          .about-main-title .gold {
            white-space: nowrap;
          }
          .about-title-line {
            white-space: normal;
          }
          .about-title-service-pc {
            display: none;
          }
          .about-title-service-sp {
            display: inline;
          }
          .about-main-copy {
            margin-top: 24px;
            font-size: 15.5px;
            line-height: 1.82;
          }
          .about-copy-spacer {
            height: 12px;
          }
          .about-download-button {
            margin-top: 28px;
            min-height: 54px;
            width: 100%;
            padding: 0 20px;
            border-radius: 15px;
            font-size: 16px;
            box-shadow: 0 8px 16px rgba(192, 113, 7, 0.12);
          }
          .about-download-button svg {
            width: 22px;
            height: 22px;
          }
          .about-compare-card {
            margin-top: 36px;
            padding: 24px;
            border-radius: 24px;
            box-shadow: 0 12px 30px rgba(32, 28, 84, 0.07);
          }
          .about-compare-grid {
            grid-template-columns: 1fr;
            gap: 22px;
          }
          .about-compare-column-nunt {
            padding-left: 0;
            border-left: 0;
            padding-top: 2px;
          }
          .about-compare-heading {
            margin-bottom: 14px;
            font-size: 20px;
            text-align: left;
          }
          .about-compare-column-nunt .about-compare-heading {
            font-size: 22px;
            margin-bottom: 16px;
          }
          .about-compare-list {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px;
          }
          .about-compare-item {
            min-height: 70px;
            padding: 9px 8px;
            border-radius: 14px;
            flex-direction: column;
            justify-content: center;
            gap: 6px;
            text-align: center;
            color: #253154;
            background: rgba(248, 249, 253, 0.82);
            border-color: rgba(116, 105, 216, 0.12);
            font-size: 13px;
            font-weight: 700;
          }
          .about-compare-item svg {
            width: 23px;
            height: 23px;
            color: #2f3a62;
          }
          .about-compare-arrow {
            width: 100%;
            height: 40px;
            display: flex;
            flex-direction: column;
            gap: 2px;
            transform: none;
          }
          .about-compare-arrow svg {
            transform: rotate(90deg);
            width: 24px;
            height: 24px;
          }
          .about-arrow-note {
            display: block;
            color: #756ee2;
            font-size: 12px;
            font-weight: 700;
            line-height: 1.2;
          }
          .about-nunt-list {
            gap: 13px;
          }
          .about-nunt-item {
            min-height: 84px;
            padding: 0 18px;
            border-radius: 15px;
            gap: 16px;
          }
          .about-nunt-item svg {
            width: 32px;
            height: 32px;
          }
          .about-nunt-label {
            font-size: 19px;
          }
          .about-zero-item {
            min-height: 112px;
          }
          .about-zero-value {
            gap: 6px;
          }
          .about-zero-number {
            font-size: 48px;
          }
          .about-zero-currency {
            font-size: 23px;
          }
          .partnership-section {
            padding: 68px 0 72px;
          }
          .partnership-inner {
            width: min(100% - 44px, 520px);
          }
          .partnership-header {
            margin-bottom: 34px;
            text-align: left;
          }
          .partnership-label {
            min-height: 31px;
            padding: 0 13px;
            font-size: 11px;
          }
          .partnership-title {
            margin-top: 18px;
            font-size: clamp(27px, 7.2vw, 32px);
            line-height: 1.36;
          }
          .partnership-lead {
            margin-top: 18px;
            font-size: clamp(13px, 3.45vw, 15px);
            line-height: 1.9;
          }
          .partnership-lead-pc {
            display: none;
          }
          .partnership-lead-sp {
            display: block;
          }
          .partnership-lead-sp-line {
            display: block;
            white-space: nowrap;
          }
          .pf-diagram {
            grid-template-columns: 1fr;
            gap: 18px;
            max-width: 520px;
          }
          .pf-card-side,
          .pf-card-nunt {
            width: 100%;
          }
          .pf-card-side {
            display: grid;
            grid-template-columns: auto minmax(0, 1fr);
            grid-template-areas:
              "icon heading"
              "items items";
            align-items: center;
            column-gap: 12px;
            row-gap: 12px;
            min-height: 0;
            padding: 16px;
            border-radius: 22px;
            text-align: left;
          }
          .pf-card-nunt {
            min-height: 0;
            padding: 26px 20px 24px;
            border-radius: 28px;
          }
          .pf-card-heading {
            grid-area: heading;
            width: auto;
            min-height: auto;
            justify-self: start;
            padding: 0;
            background: transparent;
            font-size: 21px;
            line-height: 1.35;
          }
          .pf-primary-icon {
            grid-area: icon;
            width: 44px;
            height: 44px;
            margin: 0;
          }
          .pf-primary-icon svg {
            width: 27px;
            height: 27px;
          }
          .pf-card-divider {
            display: none;
          }
          .pf-item-list {
            grid-area: items;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px;
          }
          .pf-item {
            justify-content: center;
            min-height: 40px;
            gap: 7px;
            padding: 7px 8px;
            border-radius: 13px;
            font-size: 14.5px;
            line-height: 1.35;
            text-align: center;
          }
          .pf-item-icon {
            width: 28px;
            height: 28px;
          }
          .pf-arr-h { display: none; }
          .pf-arr-v { display: block; }
          .pf-nunt-logo {
            height: 56px;
          }
          .pf-gold-rule {
            margin: 10px 0 18px;
          }
          .pf-nunt-copy {
            font-size: clamp(25px, 6.8vw, 29px);
            line-height: 1.34;
          }
          .pf-nunt-subrule {
            margin: 18px 0 14px;
          }
          .pf-feature-grid {
            gap: 8px;
          }
          .pf-feature {
            min-height: 58px;
            gap: 8px;
            padding: 8px;
            border-radius: 14px;
            font-size: 14.5px;
          }
          .pf-feature-icon {
            width: 34px;
            height: 34px;
          }
          .preview-reasons-section {
            padding: 50px 0 54px !important;
          }
          .preview-reasons-inner {
            width: min(100% - 40px, 520px);
          }
          .preview-reasons-header {
            margin-bottom: 24px;
            text-align: left;
          }
          .preview-reasons-title {
            font-size: clamp(26px, 7vw, 30px);
            line-height: 1.42;
          }
          .preview-reasons-lead {
            margin-top: 14px;
            font-size: 14px;
            line-height: 1.78;
          }
          .usecase-section {
            padding: 50px 0 54px;
          }
          .usecase-inner {
            width: min(100% - 40px, 520px);
          }
          .usecase-header {
            margin-bottom: 24px;
            text-align: left;
          }
          .usecase-lead {
            margin-top: 14px;
          }
          .preview-reasons-list {
            gap: 18px;
          }
          .preview-reason-card,
          .preview-reason-card.is-reverse {
            grid-template-columns: 1fr;
            gap: 15px;
            min-height: 0;
            padding: 16px;
            border-radius: 20px;
          }
          .preview-reason-card::before,
          .preview-reason-card.is-reverse::before {
            background: linear-gradient(180deg, rgba(246, 244, 255, 0.72), rgba(246, 244, 255, 0) 46%);
          }
          .preview-reason-media,
          .preview-reason-card.is-reverse .preview-reason-media {
            order: -1;
          }
          .preview-reason-media {
            border-radius: 15px;
            aspect-ratio: 16 / 9.4;
          }
          .preview-reason-media.contain img {
            padding: 0;
          }
          .preview-reason-copy {
            grid-template-columns: 4px minmax(0, 1fr);
            gap: 13px;
            padding: 0;
          }
          .preview-reason-line {
            width: 4px;
            min-height: 112px;
            box-shadow: 0 0 0 4px rgba(216, 132, 9, 0.08);
          }
          .preview-reason-number {
            min-height: 25px;
            padding: 0 10px;
            font-size: 11px;
          }
          .preview-reason-heading {
            margin-top: 11px;
            font-size: 18.5px;
            line-height: 1.44;
          }
          .preview-reason-heading-line,
          .preview-reason-text-line {
            display: block;
          }
          .preview-reason-text {
            margin-top: 9px;
            font-size: 13px;
            line-height: 1.72;
          }
          .preview-faq-section {
            padding: 52px 0 !important;
          }
          .preview-faq-inner {
            width: calc(100vw - 32px);
          }
          .preview-faq-label-wrap {
            text-align: left;
          }
          .preview-faq-title {
            margin-bottom: 28px;
            font-size: 24px !important;
            text-align: left;
          }
          .preview-faq-list {
            gap: 10px;
          }
          .preview-faq-button {
            align-items: flex-start;
            padding: 16px 15px;
          }
          .preview-faq-question {
            font-size: 14px;
            line-height: 1.58;
          }
          .preview-faq-answer {
            padding: 0 46px 18px 15px;
            font-size: 13px;
            line-height: 1.82;
          }
          .preview-partner-section {
            padding: 52px 0 58px !important;
          }
          .preview-partner-card {
            width: calc(100vw - 32px);
            grid-template-columns: 1fr;
            grid-template-areas:
              "copy"
              "media"
              "cta";
            gap: 22px;
            padding: 22px;
          }
          .preview-partner-title {
            font-size: 23px;
            line-height: 1.42;
          }
          .preview-partner-text {
            font-size: 13.5px;
            line-height: 1.82;
          }
          .preview-partner-chips {
            margin-top: 18px;
          }
          .preview-partner-media {
            aspect-ratio: 16 / 10.5;
          }
          .preview-partner-cta {
            width: 100%;
            min-height: 46px;
          }
        }

        @media (max-width: 390px) {
          .preview-mobile-header-inner { padding: 0 14px; gap: 9px; }
          .preview-mobile-logo { width: 98px; }
          .preview-mobile-download {
            min-height: 38px;
            padding: 0 11px;
            font-size: 12px;
          }
          .preview-menu-button { width: 36px; height: 36px; }
          .preview-hero-inner { width: calc(100vw - 38px); max-width: 520px; }
          .preview-hero-title { font-size: clamp(28px, 7.6vw, 31px) !important; }
          .preview-hero-cta { font-size: 15.5px; }
          .preview-model-image { height: 122px; }
          .preview-card-body,
          .preview-model-body { padding: 12px 13px; }
          .about-preview-inner {
            width: calc(100vw - 40px);
          }
          .about-main-title {
            font-size: clamp(31px, 8.7vw, 34px);
          }
          .about-main-copy {
            font-size: 15px;
          }
          .about-compare-card {
            padding: 22px;
          }
          .partnership-inner {
            width: calc(100vw - 40px);
          }
          .partnership-title {
            font-size: clamp(26px, 7vw, 30px);
          }
          .preview-reasons-inner {
            width: calc(100vw - 36px);
          }
          .preview-reason-card,
          .preview-reason-card.is-reverse {
            padding: 15px;
            gap: 14px;
          }
          .preview-reason-heading {
            font-size: 18px;
          }
          .preview-reason-text {
            font-size: 12.8px;
          }
          .preview-faq-inner {
            width: calc(100vw - 30px);
          }
          .preview-faq-button {
            padding: 15px 14px;
          }
          .preview-faq-answer {
            padding-right: 42px;
            padding-left: 14px;
          }
          .preview-partner-card {
            width: calc(100vw - 30px);
            padding: 18px;
            gap: 20px;
          }
          .preview-partner-title {
            font-size: 21px;
          }
          .preview-partner-text {
            font-size: 13px;
          }
          .preview-partner-chip {
            min-height: 30px;
            padding: 0 11px;
            font-size: 12px;
          }
          .pf-card-side {
            padding: 15px;
          }
          .pf-card-nunt {
            padding: 24px 18px 22px;
          }
          .pf-feature {
            font-size: 14px;
          }
        }

        @media (max-width: 360px) {
          .preview-mobile-download { padding: 0 10px; }
          .preview-mobile-download svg { display: none; }
          .preview-hero-title { font-size: 30px !important; }
          .preview-benefits { grid-template-columns: 1fr; gap: 6px; border-top: 0; padding-top: 0; }
          .preview-benefit {
            min-height: 40px;
            align-items: center;
            justify-content: flex-start;
            gap: 9px;
            text-align: left;
            border: 1px solid rgba(116, 105, 216, 0.12);
            border-radius: 12px;
            padding: 7px 10px;
            background: rgba(255,255,255,0.74);
          }
          .preview-benefit svg { width: 21px; height: 21px; margin-bottom: 0; }
          .preview-benefit strong { font-size: 11.8px; }
          .preview-visual-grid { grid-template-columns: 1fr; }
          .preview-card-female,
          .preview-card-scene,
          .preview-card-flow,
          .preview-card-corporate,
          .preview-card-audience { grid-column: 1 / -1; }
          .preview-card-audience { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* ========== NAVBAR (PC) ========== */}
      <header className="pc-nav preview-header">
        <div className="preview-header-inner">
          <a href={root} aria-label="Nunt トップへ">
            <img className="preview-logo" src="/nuntlogo.png" alt="Nunt" />
          </a>
          <nav className="preview-nav" aria-label="Nunt site navigation">
            {navLinks.map(([l,h]) => (
              <a key={l} href={h}>{l}</a>
            ))}
          </nav>
          <div className="preview-header-actions">
            <div style={{ display: "flex", alignItems: "center", gap: 3, marginRight: 6 }}>
              {[['th','TH'],['en','EN'],['jp','JP']].map(([l, label], i) => (
                <span key={l} style={{ display: "flex", alignItems: "center" }}>
                  {i > 0 && <span style={{ color: "#d0d0d0", fontSize: 10, margin: "0 2px" }}>/</span>}
                  <a href={switchLangUrl(l)} onClick={() => saveLangPref(l)} style={{
                    fontSize: 11, fontWeight: language === l ? 900 : 600,
                    color: language === l ? "#061845" : "#bbb",
                    textDecoration: language === l ? "underline" : "none",
                    textUnderlineOffset: "2px", padding: "4px 4px", lineHeight: 1,
                  }}>{label}</a>
                </span>
              ))}
            </div>
            <a className="preview-btn preview-btn-outline" href={contact}>
              <HeroIcon name="chat" size={20} />
              {tr.navContact}
            </a>
            <a className="preview-btn preview-btn-gold" href={download}>
              <HeroIcon name="document" size={21} />
              {tr.navDownload}
            </a>
          </div>
        </div>
      </header>

      {/* ========== NAVBAR (SP) ========== */}
      <header className="sp-nav preview-mobile-header">
        <div className="preview-mobile-header-inner">
          <a href={root} aria-label="Nunt トップへ">
            <img className="preview-mobile-logo" src="/nuntlogo.png" alt="Nunt" />
          </a>
          <a className="preview-mobile-download" href={download}>
            <HeroIcon name="document" size={22} />
            <span>{tr.navDownload}</span>
          </a>
          <button
            className="preview-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={isEn ? "Open menu" : "メニューを開く"}
            aria-expanded={menuOpen}
            type="button"
          >
            <svg width="34" height="27" viewBox="0 0 34 27" fill="none" aria-hidden="true">
              <rect x="2" y="3" width="30" height="3" rx="1.5" fill="currentColor" />
              <rect x="2" y="12" width="30" height="3" rx="1.5" fill="currentColor" />
              <rect x="2" y="21" width="30" height="3" rx="1.5" fill="currentColor" />
            </svg>
          </button>
        </div>
        {menuOpen && (
          <nav className="preview-mobile-menu" aria-label="Nunt mobile navigation">
            {navLinks.map(([l,h]) => (
              <a key={l} href={h}>{l}</a>
            ))}
            <div style={{ borderTop: "1px solid rgba(6, 24, 69, 0.08)", paddingTop: 4, marginTop: 2 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#999", padding: "10px 0 4px", letterSpacing: "0.06em" }}>Language</p>
              {[['th','TH'],['en','EN'],['jp','JP']].map(([l, label]) => (
                <a key={l} href={switchLangUrl(l)} onClick={() => handleLangSwitch(l)}
                  style={{ fontWeight: language === l ? 900 : 600, color: language === l ? "#061845" : "#888" }}>
                  {label}
                </a>
              ))}
            </div>
          </nav>
        )}
      </header>

      {/* ========== HERO (PC) ========== */}
      <section className="hero-pc preview-hero">
        <div className="preview-hero-inner">
          <div className="preview-hero-copy">
            <div className="preview-eyebrow">{tr.heroEyebrow}</div>
            <h1 className="preview-hero-title">
              {isTh ? (
                <>
                  ใช้ภาพทาเลนต์<span className="preview-mobile-break"><br /></span>ในเว็บไซต์<br />
                  และโฆษณา<span className="preview-mobile-break"><br /></span>ได้ง่ายขึ้น
                </>
              ) : isEn ? (
                <>
                  Talent visuals for<span className="preview-mobile-break"><br /></span>websites<br />
                  and advertising<span className="preview-mobile-break"><br /></span>in Thailand.
                </>
              ) : (
                <>
                  タイのタレント<span className="preview-mobile-break"><br /></span>素材を、<br />
                  Web・広告で<span className="preview-mobile-break"><br /></span>使いやすく。
                </>
              )}
            </h1>
            <p className="preview-hero-subcopy">
              {isTh ? (
                <>
                  Nunt คือบริการใช้ภาพทาเลนต์แบบรายเดือน<span className="preview-mobile-break"><br /></span>สำหรับบริษัทในไทย<br />
                  สามารถนำภาพทาเลนต์ไปใช้กับเว็บไซต์<span className="preview-mobile-break"><br /></span>โซเชียลมีเดีย และโฆษณาได้<br />
                  โดยไม่มีค่าเริ่มต้น จ่ายเฉพาะค่าบริการรายเดือน
                </>
              ) : isEn ? (
                <>
                  Nunt is a monthly talent visual licensing service<span className="preview-mobile-break"><br /></span>for companies in Thailand.<br />
                  You can use ready-to-use talent visuals for websites,<span className="preview-mobile-break"><br /></span>social media, and advertising<br />
                  with no initial fee, only a monthly plan.
                </>
              ) : (
                <>
                  Nuntは、タイ法人向けの<span className="preview-mobile-break"><br /></span>タレント素材月額サービスです。<br />
                  Webサイト・SNS・広告などで<span className="preview-mobile-break"><br /></span>使える素材を、<br />
                  初期費用0 THB・月額のみでご利用いただけます。
                </>
              )}
            </p>
            <div className="preview-cta-row">
              <a className="preview-btn preview-btn-gold preview-hero-cta" href={download}>
                <span><HeroIcon name="document" size={22} />{tr.heroCta1}</span>
                <HeroIcon name="arrow" size={22} />
              </a>
              <a className="preview-btn preview-btn-outline preview-hero-cta" href={contact}>
                <span><HeroIcon name="chat" size={22} />{tr.heroCta2}</span>
                <HeroIcon name="arrow" size={22} />
              </a>
            </div>
            <a className="preview-support-link" href={contact}>
              <HeroIcon name="users" size={32} />
              <span>
                <strong>{tr.heroSupportTitle}</strong>
                <span>{tr.heroSupportSub}</span>
              </span>
              <HeroIcon className="preview-support-arrow" name="arrow" size={22} />
            </a>
            <div className="preview-benefits" aria-label="Nuntの強み">
              {benefitsPC.map(([icon, line1, line2], index) => (
                <div key={line1} className={index > 2 ? "preview-benefit preview-benefit-optional" : "preview-benefit"}>
                  <HeroIcon name={icon} size={34} strokeWidth={1.6} />
                  <strong>
                    <span className="preview-benefit-line">{line1}</span>
                    {line2 ? <span className="preview-benefit-line">{line2}</span> : null}
                  </strong>
                </div>
              ))}
            </div>
          </div>

          <div className="preview-visual-grid" aria-label="Nuntの利用イメージ">
            <article className="preview-card preview-image-card preview-card-female">
              <img className="preview-model-image" src="/brand-sample.png" alt="スキンケアブランドのタレント素材例" style={{ objectPosition: "38% 8%" }} />
              <div className="preview-model-body">
                <p className="preview-card-kicker">Beauty<br />Campaign</p>
                <p className="preview-model-title">{tr.heroModelTitle1a}<br />{tr.heroModelTitle1b}</p>
                <MiniProgress />
              </div>
            </article>

            <article className="preview-card preview-card-scene">
              <div className="preview-card-body">
                <h2 className="preview-card-heading">{tr.heroSceneTitle}</h2>
                <div className="preview-scene-list">
                  {sceneItems.map(([icon, label]) => (
                    <div className="preview-scene-item" key={label}>
                      <span className="preview-scene-icon"><HeroIcon name={icon} size={18} /></span>
                      {label}
                    </div>
                  ))}
                </div>
                <p className="preview-card-note">{tr.heroSceneNote}</p>
              </div>
            </article>

            <article className="preview-card preview-image-card preview-card-male">
              <img className="preview-model-image" src="/images/nunt-male-talent.png" alt="ライフスタイルブランドの男性モデル素材例" style={{ objectPosition: "50% 12%" }} />
              <div className="preview-model-body">
                <p className="preview-card-kicker">Lifestyle<br />Brand</p>
                <p className="preview-model-title">
                  <span className="preview-model-title-line">{tr.heroModelTitle2a}</span>
                  <span className="preview-model-title-line">{tr.heroModelTitle2b}</span>
                </p>
                <MiniProgress />
              </div>
            </article>

            <article className="preview-card preview-card-flow">
              <div className="preview-card-body">
                <h2 className="preview-card-heading">{tr.heroFlowTitle}</h2>
                <div className="preview-flow-list">
                  {flowItems.map((label, index) => (
                    <div className="preview-flow-item" key={label}>
                      <span className="preview-flow-number">{String(index + 1).padStart(2, "0")}</span>
                      {label}
                    </div>
                  ))}
                </div>
                <p className="preview-card-note">{tr.heroFlowNote}</p>
              </div>
            </article>

            <article className="preview-card preview-card-corporate">
              <img className="preview-corporate-image" src={collab} alt="企業向けコミュニケーションの利用イメージ" />
              <div className="preview-card-body">
                <h2 className="preview-card-heading">Corporate / Service</h2>
                <p className="preview-corporate-copy">
                  {isTh ? (
                    "ใช้สื่อสารความน่าเชื่อถือ เช่น แนะนำบริการ รับสมัครงาน PR และภาพลักษณ์องค์กร"
                  ) : isEn ? (
                    "For service introductions, hiring, PR, and other trust-building communications."
                  ) : (
                    <>サービス紹介・採用・広報など<br />信頼性を高めるコミュニケーションに</>
                  )}
                </p>
                <MiniProgress />
              </div>
            </article>

            <div className="preview-card preview-card-audience">
              <a className="preview-audience-link" href={download}>
                <HeroIcon name="building" size={36} strokeWidth={1.6} />
                <span>
                  <strong>{tr.heroAudience1Title}</strong>
                  <span>{tr.heroAudience1SubPC}</span>
                </span>
                <HeroIcon name="arrow" size={22} />
              </a>
              <a className="preview-audience-link" href={contact}>
                <HeroIcon name="users" size={38} strokeWidth={1.6} />
                <span>
                  <strong className="preview-audience-heading-nowrap">{tr.heroAudience2Title}</strong>
                  <span>{tr.heroAudience2Sub}</span>
                </span>
                <HeroIcon name="arrow" size={22} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== HERO (SP) ========== */}
      <section className="hero-sp preview-mobile-hero">
        <div className="preview-hero-inner">
          <div className="preview-hero-copy">
            <div className="preview-eyebrow">{tr.heroEyebrowSP}</div>
            <h1 className="preview-hero-title">
              {isTh ? (
                <>
                  ใช้ภาพทาเลนต์<br />
                  ในเว็บไซต์<br />
                  และโฆษณาได้ง่ายขึ้น
                </>
              ) : isEn ? (
                <>
                  Talent visuals for<br />
                  websites and<br />
                  advertising in Thailand.
                </>
              ) : (
                <>
                  タイのタレント素材を、<br />
                  Web・広告で<br />
                  使いやすく。
                </>
              )}
            </h1>
            <p className="preview-hero-subcopy">
              {isTh ? (
                <>
                  Nunt คือบริการใช้ภาพทาเลนต์<br />
                  แบบรายเดือนสำหรับบริษัทในไทย
                  <span className="preview-copy-spacer" aria-hidden="true" />
                  นำภาพไปใช้กับเว็บไซต์ โซเชียลมีเดีย<br />
                  และโฆษณาได้ โดยไม่มีค่าเริ่มต้น
                </>
              ) : isEn ? (
                <>
                  Nunt is a monthly talent visual licensing<br />
                  service for companies in Thailand.
                  <span className="preview-copy-spacer" aria-hidden="true" />
                  Use ready-to-use talent visuals for websites,<br />
                  social media, and advertising with no initial fee.
                </>
              ) : (
                <>
                  Nuntは、タイ法人向けの<br />
                  タレント素材月額サービスです。
                  <span className="preview-copy-spacer" aria-hidden="true" />
                  Webサイト・SNS・広告などで使える素材を、<br />
                  初期費用0 THB・月額のみでご利用いただけます。
                </>
              )}
            </p>
            <div className="preview-cta-row">
              <a className="preview-btn preview-btn-gold preview-hero-cta" href={download}>
                <span><HeroIcon name="document" size={22} />{tr.heroCta1}</span>
                <HeroIcon name="arrow" size={22} />
              </a>
              <a className="preview-btn preview-btn-outline preview-hero-cta" href={contact}>
                <span><HeroIcon name="chat" size={22} />{tr.heroCta2}</span>
                <HeroIcon name="arrow" size={22} />
              </a>
            </div>
            <a className="preview-support-link" href={contact}>
              <HeroIcon name="users" size={32} />
              <span>
                <strong>{tr.heroSupportTitle}</strong>
                <span>{tr.heroSupportSub}</span>
              </span>
              <HeroIcon className="preview-support-arrow" name="arrow" size={22} />
            </a>
            <div className="preview-benefits" aria-label="Nuntの強み">
              {benefitsSP.map(([icon, line1, line2]) => (
                <div key={line1} className="preview-benefit">
                  <HeroIcon name={icon} size={34} strokeWidth={1.6} />
                  <strong>
                    <span className="preview-benefit-line">{line1}</span>
                    {line2 ? <span className="preview-benefit-line">{line2}</span> : null}
                  </strong>
                </div>
              ))}
            </div>
          </div>

          <div className="preview-visual-grid" aria-label="Nuntの利用イメージ">
            <article className="preview-card preview-image-card preview-card-female">
              <img className="preview-model-image" src="/brand-sample.png" alt="スキンケアブランドのタレント素材例" style={{ objectPosition: "38% 8%" }} />
              <div className="preview-model-body">
                <p className="preview-model-title">{tr.heroModelTitle1a}<br />{tr.heroModelTitle1b}</p>
                <MiniProgress />
              </div>
            </article>

            <article className="preview-card preview-card-scene">
              <div className="preview-card-body">
                <h2 className="preview-card-heading">{tr.heroSceneTitle}</h2>
                <div className="preview-scene-list">
                  {sceneItems.map(([icon, label]) => (
                    <div className="preview-scene-item" key={label}>
                      <span className="preview-scene-icon"><HeroIcon name={icon} size={18} /></span>
                      {label}
                    </div>
                  ))}
                </div>
                <p className="preview-card-note">{tr.heroSceneNote}</p>
              </div>
            </article>

            <article className="preview-card preview-card-flow">
              <div className="preview-card-body">
                <h2 className="preview-card-heading">{tr.heroFlowTitle}</h2>
                <div className="preview-flow-list">
                  {flowItems.map((label, index) => (
                    <div className="preview-flow-item" key={label}>
                      <span className="preview-flow-number">{String(index + 1).padStart(2, "0")}</span>
                      {label}
                    </div>
                  ))}
                </div>
                <p className="preview-card-note">{tr.heroFlowNote}</p>
              </div>
            </article>

            <article className="preview-card preview-card-corporate">
              <img className="preview-corporate-image" src={collab} alt="企業向けコミュニケーションの利用イメージ" />
              <div className="preview-card-body">
                <h2 className="preview-card-heading">Corporate / Service</h2>
                <p className="preview-corporate-copy">
                  {isTh ? (
                    "ใช้สื่อสารความน่าเชื่อถือ เช่น แนะนำบริการ รับสมัครงาน PR และภาพลักษณ์องค์กร"
                  ) : isEn ? (
                    "For service introductions, hiring, PR, and other trust-building communications."
                  ) : (
                    <>サービス紹介・採用・広報など<br />信頼性を高めるコミュニケーションに</>
                  )}
                </p>
                <MiniProgress />
              </div>
            </article>

            <div className="preview-card preview-card-audience">
              <a className="preview-audience-link" href={download}>
                <HeroIcon name="building" size={36} strokeWidth={1.6} />
                <span>
                  <strong>{tr.heroAudience1Title}</strong>
                  <span>{tr.heroAudience1SubSP}</span>
                </span>
                <HeroIcon name="arrow" size={22} />
              </a>
              <a className="preview-audience-link" href={contact}>
                <HeroIcon name="users" size={38} strokeWidth={1.6} />
                <span>
                  <strong>{tr.heroAudience2Title}</strong>
                  <span>{tr.heroAudience2Sub}</span>
                </span>
                <HeroIcon name="arrow" size={22} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== Nuntとは ========== */}
      <section id="about" className="about-preview-section">
        <div className={`about-preview-inner${isTh ? ' about-preview-inner-th' : isEn ? ' about-preview-inner-en' : ''}`}>
          <div className="about-preview-copy">
            <h2 className="about-section-label">{tr.aboutLabel}</h2>
            <p className={`about-main-title${isTh ? ' about-main-title-th' : ''}`}>
              {isTh ? (
                <>
                  <span className="about-title-line">บริการใช้ภาพทาเลนต์รายเดือน</span><br />
                  <span className="gold about-title-line about-title-service-pc">โดยไม่มีค่าเริ่มต้น 0 THB</span>
                  <span className="gold about-title-line about-title-service-sp">โดยไม่มีค่าเริ่มต้น 0 THB</span>
                </>
              ) : isEn ? (
                <>
                  <span className="about-title-line">A monthly talent</span><br />
                  <span className="about-title-line">visual service with</span><br />
                  <span className="gold about-title-line about-title-service-pc">0 THB initial fee.</span>
                  <span className="gold about-title-line about-title-service-sp">0 THB initial fee.</span>
                </>
              ) : (
                <>
                  <span className="gold about-title-line">初期費用 0 THB。</span><br />
                  <span className="about-title-line">月額だけで使える、</span><br />
                  <span className="about-title-line about-title-service-pc">タレント素材活用サービス。</span>
                  <span className="about-title-line about-title-service-sp">タレント素材活用<br />サービス。</span>
                </>
              )}
            </p>
            <p className="about-main-copy">
              {isTh ? (
                <>
                  Nunt ให้บริการภาพทาเลนต์ที่พร้อมใช้สำหรับเว็บไซต์ โซเชียลมีเดีย และโฆษณาแก่บริษัทในไทย
                  <span className="about-copy-spacer" aria-hidden="true" />
                  ช่วยลดเวลาและต้นทุนในการถ่ายภาพ แคสติ้ง และการเจรจาแยกเป็นรายกรณี ทำให้บริษัทเริ่มใช้ภาพได้อย่างราบรื่น
                </>
              ) : isEn ? (
                <>
                  Nunt provides ready-to-use talent visuals for websites, social media, and advertising to companies in Thailand.
                  <span className="about-copy-spacer" aria-hidden="true" />
                  It helps reduce the time and cost of photo shoots, casting, and individual negotiations, so companies can start using the visuals smoothly.
                </>
              ) : (
                <>
                  Nuntは、タイ法人向けに<br />
                  Webサイト・SNS・広告で使える<br />
                  タレント素材を提供する月額サービスです。<br />
                  <span className="about-copy-spacer" aria-hidden="true" />
                  撮影や個別交渉の手間を抑え、<br />
                  必要な素材をすぐに活用できます。
                </>
              )}
            </p>
            <a className="about-download-button" href={download}>
              <HeroIcon name="document" size={22} />
              <span>{tr.aboutDownloadBtn}</span>
              <HeroIcon name="arrow" size={24} />
            </a>
          </div>

          <div className="about-compare-card" aria-label="通常のタレント起用とNunt利用の比較">
            <div className="about-compare-grid">
              <div className="about-compare-column">
                <h3 className="about-compare-heading">{tr.aboutCompareHeading}</h3>
                <div className="about-compare-list">
                  {[
                    ["camera", tr.aboutCompareShoot],
                    ["document", tr.aboutCompareContract],
                    ["monitor", tr.aboutCompareMaterial],
                    ["baht", tr.aboutCompareCost],
                  ].map(([icon, label]) => (
                    <div className="about-compare-item" key={label}>
                      <HeroIcon name={icon} strokeWidth={1.7} />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="about-compare-arrow" aria-hidden="true">
                <HeroIcon name="arrow" size={42} strokeWidth={1.8} />
                <span className="about-arrow-note">{tr.aboutArrowNote}</span>
              </div>

              <div className="about-compare-column about-compare-column-nunt">
                <h3 className="about-compare-heading">{tr.aboutNuntHeading}</h3>
                <div className="about-nunt-list">
                  <div className="about-nunt-item">
                    <HeroIcon name="card" strokeWidth={1.7} />
                    <span className="about-nunt-label">{tr.aboutMonthly}</span>
                  </div>
                  <div className="about-nunt-item about-zero-item">
                    <HeroIcon name="baht" strokeWidth={1.7} />
                    <span className="about-zero-copy">
                      <span className="about-nunt-label">{tr.aboutInitialLabel}</span>
                      <span className="about-zero-value">
                        <span className="about-zero-number">0</span>
                        <span className="about-zero-currency">THB</span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 提携モデル ========== */}
      <section className="partnership-section">
        <div className="partnership-inner">
          <div className="partnership-header">
            <span className="partnership-label">{tr.partnershipLabel}</span>
            <h2 className="partnership-title">
              {isTh ? (
                <>
                  โครงสร้างที่มั่นใจได้สำหรับทั้งบริษัท<br />
                  และพาร์ทเนอร์ทาเลนต์
                </>
              ) : isEn ? (
                <>
                  A safe structure for both<br />
                  companies and talent partners.
                </>
              ) : (
                <>
                  企業・タレント双方に安心な、<br />
                  素材活用の仕組み。
                </>
              )}
            </h2>
            <p className="partnership-lead">
              {isTh ? (
                <>
                  <span className="partnership-lead-pc">
                    Nunt ทำงานร่วมกับทาเลนต์ เอเจนซี่ และอินฟลูเอนเซอร์<br />
                    จากนั้นจัดระเบียบภาพและเงื่อนไขการใช้งานให้อยู่ในรูปแบบที่บริษัทใช้งานได้ง่าย<br />
                    ช่วยลดขั้นตอนการเจรจา พร้อมให้บริษัทนำภาพไปใช้กับเว็บไซต์ โซเชียลมีเดีย และโฆษณาได้อย่างมั่นใจ
                  </span>
                  <span className="partnership-lead-sp" style={{ whiteSpace: "normal" }}>
                    Nunt ทำงานร่วมกับทาเลนต์และเอเจนซี่ เพื่อจัดระเบียบภาพและเงื่อนไขการใช้งาน บริษัทจึงนำไปใช้กับเว็บไซต์ SNS และโฆษณาได้อย่างมั่นใจ
                  </span>
                </>
              ) : isEn ? (
                <>
                  <span className="partnership-lead-pc">
                    Nunt works with talents, agencies, and influencers, then organizes the visuals and usage conditions<br />
                    in a way that companies can use easily.<br />
                    This helps reduce individual negotiations while allowing companies to use the visuals safely across web, social media, and advertising.
                  </span>
                  <span className="partnership-lead-sp" style={{ whiteSpace: "normal" }}>
                    Nunt works with talents and agencies to organize visuals and usage conditions. Companies can use the materials safely for websites, SNS, and ads.
                  </span>
                </>
              ) : (
                <>
                  <span className="partnership-lead-pc">
                    Nuntは、タレント・事務所・インフルエンサーとの提携をもとに、<br />
                    企業が使いやすい形で素材と利用条件を整理・提供します。<br />
                    個別交渉や確認の手間を抑えながら、Web・SNS・広告で安心して活用できます。
                  </span>
                  <span className="partnership-lead-sp">
                    <span className="partnership-lead-sp-line">Nuntは、タレント・事務所との提携をもとに、</span>
                    <span className="partnership-lead-sp-line">素材と利用条件を整理して提供します。</span>
                    <span className="partnership-lead-sp-line">企業はWeb・SNS・広告で安心して活用できます。</span>
                  </span>
                </>
              )}
            </p>
          </div>

          <div className="pf-diagram" aria-label="タレント・事務所、Nunt、企業の提携モデル">
            <article className="pf-card pf-card-side">
              <h3 className="pf-card-heading">{tr.partnershipTalent}</h3>
              <div className="pf-primary-icon" aria-hidden="true">
                <HeroIcon name="users" strokeWidth={1.45} />
              </div>
              <div className="pf-card-divider" />
              <div className="pf-item-list">
                <div className="pf-item">
                  <span className="pf-item-icon" aria-hidden="true">
                    <HeroIcon name="image" size={28} strokeWidth={1.65} />
                  </span>
                  <span>{tr.partnershipMaterial}</span>
                </div>
                <div className="pf-item">
                  <span className="pf-item-icon" aria-hidden="true">
                    <HeroIcon name="fileCheck" size={28} strokeWidth={1.65} />
                  </span>
                  <span>{tr.partnershipCondition}</span>
                </div>
              </div>
            </article>

            <div className="pf-flow-arrow" aria-hidden="true">
              <svg className="pf-arr-h" viewBox="0 0 48 18" fill="none">
                <line className="pf-arrow-line" x1="3" y1="9" x2="40" y2="9" />
                <polyline className="pf-arrow-head" points="33,3 41,9 33,15" fill="none" />
              </svg>
              <svg className="pf-arr-v" viewBox="0 0 18 48" fill="none">
                <line className="pf-arrow-line" x1="9" y1="3" x2="9" y2="40" />
                <polyline className="pf-arrow-head" points="3,33 9,41 15,33" fill="none" />
              </svg>
            </div>

            <article className="pf-card pf-card-nunt">
              <img src="/images/nuntlogo.png" alt="Nunt" className="pf-nunt-logo" loading="eager" decoding="async" />
              <div className="pf-gold-rule" aria-hidden="true">
                <span className="pf-gold-star" />
              </div>
              <h3 className="pf-nunt-copy">
                {tr.partnershipNuntCopy1}<br />
                {tr.partnershipNuntCopy2}
              </h3>
              <div className="pf-nunt-subrule" aria-hidden="true">
                <span className="pf-nunt-dot" />
              </div>
              <div className="pf-feature-grid">
                <div className="pf-feature">
                  <span className="pf-feature-icon" aria-hidden="true">
                    <HeroIcon name="users" size={26} strokeWidth={1.65} />
                  </span>
                  <span>{tr.partnershipManage}</span>
                </div>
                <div className="pf-feature">
                  <span className="pf-feature-icon" aria-hidden="true">
                    <HeroIcon name="folder" size={26} strokeWidth={1.65} />
                  </span>
                  <span>{tr.partnershipOrganize}</span>
                </div>
                <div className="pf-feature">
                  <span className="pf-feature-icon" aria-hidden="true">
                    <HeroIcon name="shieldCheck" size={26} strokeWidth={1.65} />
                  </span>
                  <span>{tr.partnershipScope}</span>
                </div>
                <div className="pf-feature">
                  <span className="pf-feature-icon" aria-hidden="true">
                    <HeroIcon name="send" size={26} strokeWidth={1.65} />
                  </span>
                  <span>{tr.partnershipProposal}</span>
                </div>
              </div>
            </article>

            <div className="pf-flow-arrow" aria-hidden="true">
              <svg className="pf-arr-h" viewBox="0 0 48 18" fill="none">
                <line className="pf-arrow-line" x1="3" y1="9" x2="40" y2="9" />
                <polyline className="pf-arrow-head" points="33,3 41,9 33,15" fill="none" />
              </svg>
              <svg className="pf-arr-v" viewBox="0 0 18 48" fill="none">
                <line className="pf-arrow-line" x1="9" y1="3" x2="9" y2="40" />
                <polyline className="pf-arrow-head" points="3,33 9,41 15,33" fill="none" />
              </svg>
            </div>

            <article className="pf-card pf-card-side">
              <h3 className="pf-card-heading">{tr.partnershipCompany}</h3>
              <div className="pf-primary-icon" aria-hidden="true">
                <HeroIcon name="building" strokeWidth={1.45} />
              </div>
              <div className="pf-card-divider" />
              <div className="pf-item-list">
                <div className="pf-item">
                  <span className="pf-item-icon" aria-hidden="true">
                    <HeroIcon name="chat" size={28} strokeWidth={1.65} />
                  </span>
                  <span>{tr.partnershipConsult}</span>
                </div>
                <div className="pf-item">
                  <span className="pf-item-icon" aria-hidden="true">
                    <HeroIcon name="monitor" size={28} strokeWidth={1.65} />
                  </span>
                  <span>{tr.partnershipUse}</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ========== 選ばれる3つの理由 ========== */}
      <section id="reasons" className="preview-reasons-section">
        <div className="preview-reasons-inner">
          <div className="preview-reasons-header">
            <h2 className="preview-reasons-title">{tr.reasonsTitle}</h2>
          </div>

          <div className="preview-reasons-list">
            {reasonItems.map((reason) => (
              <article
                className={`preview-reason-card${reason.reverse ? " is-reverse" : ""}`}
                key={reason.number}
              >
                <div className="preview-reason-copy">
                  <span className="preview-reason-line" aria-hidden="true" />
                  <div>
                    <span className="preview-reason-number">{reason.number}</span>
                    <h3 className="preview-reason-heading">
                      {reason.titleLines.map((line) => (
                        <span className="preview-reason-heading-line" key={line}>{line}</span>
                      ))}
                    </h3>
                    <p className="preview-reason-text">
                      {reason.bodyLines.map((line) => (
                        <span className="preview-reason-text-line" key={line}>{line}</span>
                      ))}
                    </p>
                  </div>
                </div>
                <figure className={`preview-reason-media${reason.imageFit === "contain" ? " contain" : ""}`}>
                  <img src={reason.image} alt={reason.alt} loading="lazy" decoding="async" />
                </figure>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ========== 業種に合わせた活用イメージ ========== */}
      <section id="cases" className="usecase-section">
        <div className="usecase-inner">
          <div className="usecase-header">
            <h2 className="preview-reasons-title usecase-title">
              {tr.usecaseTitle}
            </h2>
          </div>
          <div className="usecase-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {usecasesData.map((c) => (
              <div key={c.label} style={{
                background: "#fff", borderRadius: 12,
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                border: "1px solid #e8edf2",
              }}>
                {/* メイン画像 */}
                <img src={c.img} alt={c.label}
                  style={{ width: "100%", height: 180, objectFit: "cover", display: "block" }} />

                {/* テキスト */}
                <div style={{ padding: "16px 18px 20px" }}>
                  <h3 style={{ fontSize: 14, fontWeight: 900, marginBottom: 6, color: "#001A3D" }}>
                    {c.label}
                  </h3>
                  <p style={{ fontSize: 11, color: "#666", lineHeight: 1.6 }}>{c.desc}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
                    {c.chips.map((chip) => (
                      <span key={chip} className="usecase-chip">
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 活用イメージ画像エリア */}
                <div style={{ padding: "0 12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: "#8b6b22", marginBottom: 4 }}>
                    {tr.usecaseCardLabel}
                  </p>
                  {c.examples.map((ex, i) => (
                    typeof ex === "string" && !ex.startsWith("/") ? (
                      <div key={i} style={{
                        width: "100%", height: 160,
                        background: "#d9d9d9", borderRadius: 8,
                        display: "flex", alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11, color: "#888",
                      }}>
                        {ex}
                      </div>
                    ) : (
                      <img
                        key={i}
                        src={ex}
                        alt={c.label + "活用イメージ"}
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                          borderRadius: 8,
                          display: "block",
                        }}
                      />
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== ご相談前によくある質問 ========== */}
      <section id="faq" className="preview-faq-section">
        <div className="faq-inner preview-faq-inner">
          <div className="preview-faq-label-wrap">
            <span className="preview-faq-label">FAQ</span>
          </div>
          <h2 className="preview-faq-title">
            {tr.faqTitle}
          </h2>
          <div className="preview-faq-list">
            {faqs.map((item, i) => {
              const isOpen = faqOpen === i;

              return (
                <div key={i} className={`preview-faq-item${isOpen ? " is-open" : ""}`}>
                  <button
                    type="button"
                    className="preview-faq-button"
                    aria-expanded={isOpen}
                    onClick={() => setFaqOpen(isOpen ? null : i)}
                  >
                    <span className="preview-faq-question">{item.q}</span>
                    <span className="preview-faq-icon" aria-hidden="true">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="preview-faq-answer">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* ========== Nuntとの協業をご検討の方へ ========== */}
      <section className="preview-partner-section">
        <div className="preview-partner-card">
          <div className="preview-partner-copy">
            <span className="preview-partner-label">{tr.partnerLabel}</span>
            <h2 className="preview-partner-title">
              {isTh ? (
                <>
                  สำหรับผู้ที่สนใจ<span className="preview-mobile-break"><br /></span>ร่วมงานกับ Nunt
                </>
              ) : isEn ? (
                <>
                  For those interested in<span className="preview-mobile-break"><br /></span>partnering with Nunt
                </>
              ) : (
                <>
                  Nuntとの協業を<span className="preview-mobile-break"><br /></span>ご検討の方へ
                </>
              )}
            </h2>
            <p className="preview-partner-text">
              {isTh ? (
                <>
                  Nunt ทำงานร่วมกับทาเลนต์เอเจนซี่ อินฟลูเอนเซอร์ เอเจนซี่โฆษณา และบริษัทโปรดักชัน<br />
                  หากสนใจเรื่องการใช้ภาพทาเลนต์ การทำการตลาดในไทย หรือความร่วมมือด้านโปรโมชันสำหรับองค์กร สามารถติดต่อเราได้
                </>
              ) : isEn ? (
                <>
                  Nunt works with talent agencies, influencers, advertising agencies, and production companies.<br />
                  If you are interested in talent visual licensing, marketing use in Thailand, or corporate promotion partnerships, please feel free to contact us.
                </>
              ) : (
                <>
                  Nuntでは、タレント事務所・インフルエンサー・広告代理店・制作会社との連携を行っています。<br />
                  タイ市場での素材活用や、企業向けプロモーションに関する協業について、お気軽にご相談ください。
                </>
              )}
            </p>
            <div className="preview-partner-chips" aria-label="協業パートナー種別">
              {partnerChips.map((chip) => (
                <span className="preview-partner-chip" key={chip}>{chip}</span>
              ))}
            </div>
          </div>
          <figure className="preview-partner-media">
            <img className="preview-partner-image" src={partnerCollab} alt="Nuntとの協業イメージ" loading="lazy" decoding="async" />
          </figure>
          <a className="preview-partner-cta" href={contact}>
            {tr.partnerCta}
            <HeroIcon name="arrow" size={18} strokeWidth={1.9} />
          </a>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer style={{ background: "#fff", borderTop: "1px solid #eee", padding: "20px 0" }}>
        <div className="footer-inner" style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <img src="/nuntlogo.png" alt="Nunt" style={{ height: 70, width: "auto" }} />
          <nav style={{ display: "flex", gap: 24 }}>
            {[[tr.footerAbout,`${root}#about`],[tr.footerContact,contact]].map(([l,h]) => (
              <a key={l} href={h} style={{ fontSize: 11, color: "#888", textDecoration: "none" }}>{l}</a>
            ))}
            <a href={privacy} style={{ fontSize: 11, color: "#888", textDecoration: "none" }}>{tr.footerPrivacy}</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
